import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { NewsiteCommerceEntry } from "./pages/shop/newsiteEntryReadiness";
import { Switch, Route, Router as WouterRouter, useLocation, useSearch, Redirect } from "wouter";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import { ClerkProvider, useAuth, useClerk } from "@clerk/react";
import { ApiError, setAuthTokenGetter, setFetchGate } from "@workspace/api-client-react";
import { TenantSelectionBridge } from "@/lib/tenant-selection";
import {
  handleQueryErrorForThrottle,
  setThrottleQueryCache,
  throttleFetchGate,
} from "@/lib/throttle-toast";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import {
  BrandingProvider,
  PublicBrandingProvider,
} from "@/components/branding-provider";
import { AppSurfaceBoundary } from "@/components/app-surface-boundary";
import { useClerkAppearance } from "@/lib/clerk-appearance";
import { useClerkLoadWatchdog } from "@/lib/clerk-recovery";
import { DEV_TEST_AUTH_TOKEN, isDevTestAuthActive } from "@/lib/dev-test-auth";
import ClerkStuckRecovery from "@/components/clerk-stuck-recovery";
import NotFound from "@/pages/not-found";
import { MainLayout } from "@/components/layout/main-layout";
import { AccessDenied } from "@/components/access-denied";
import { FeatureLockedPage } from "@/components/feature-locked-page";
import { RouteErrorBoundary } from "@/components/route-error-boundary";
import { ReportsRouteGate } from "@/components/reports/reports-route-gate";
import { HelpDrawerProvider, VCCardGridSkeleton, VCDetailSkeleton, VCTableSkeleton } from "@/components/vc";
import { WalkthroughProvider } from "@/lib/walkthroughs";
import { PackingRecorderProvider } from "@/components/packing/packing-recorder-context";
import {
  canonicalPortfolioPath,
  isLegacyPortalPortfolioPath,
  isPortfolioAppHost,
  isPortfolioPath,
} from "@/lib/app-surface";
import { OPERATOR_MARKET_ROUTES } from "@/lib/market-route-boundary";
import PendingApprovalScreen from "@/pages/pending-approval";
import { useMe, useMyFeatures, useIsPlatformOperator, type Permission, type SectionId } from "@/lib/auth";
import { useMyPlatformPermissions } from "@/hooks/useMyPlatformPermissions";
import { canAccessAdminWorkspace } from "@/pages/admin/workspaces/access";
import { portfolioPath } from "@/pages/portal/portfolio-routing";
import type { FeatureKey } from "@workspace/api-client-react";
import { LEGACY_BRAND_CUSTOMISATION_REDIRECTS } from "@/lib/brand-customisation-routes";
import { ringAppstoreAuthReturn } from "@/lib/ring-appstore-routing";
import {
  isWhatnotFeeAffectedQuery,
  subscribeToWhatnotFeeUpdates,
} from "@/lib/whatnot-fee-invalidation";
import {
  beginRouteNavigation,
  registerRoutePreload,
  useAppPerformanceInstrumentation,
} from "@/lib/app-performance";
const PublicSite = lazy(() => import("@/pages/site/index"));

const Dashboard = lazy(() => import("@/pages/dashboard"));
const PublicMarketPage = lazy(() => import("@/pages/public/market"));
const PublicMarketWidget = lazy(() => import("@/pages/public/widget"));
const CustomerPortal = lazy(() => import("@/pages/portal/index"));
const CustomerStorefront = lazy(() => import("@/pages/shop/index"));
const Verification = lazy(() => import("@/pages/verification"));
const MobileApp = lazy(() => import("@/pages/mobile/mobile-app"));
const OperationsIndex = lazy(() => import("@/pages/operations/index"));
const OperationsSearch = lazy(() => import("@/pages/operations/search"));
const OperationsChecklists = lazy(() => import("@/pages/operations/checklists/index"));
const OperationsChecklistManage = lazy(() => import("@/pages/operations/checklists/manage"));
const OperationsFeed = lazy(() => import("@/pages/operations/feed"));
const OperationsBoard = lazy(() => import("@/pages/operations/board"));
const OperationsFriction = lazy(() => import("@/pages/operations/friction"));
// Keep the critical list chunks lazy for cold entry, but expose their existing
// dynamic-import promises to navigation-intent/idle preloading. Calling the
// same loader from React.lazy and preloadRoute is deduplicated by the browser
// module loader; it does not turn either route into an eager import.
const loadInventoryList = () => import("@/pages/inventory/index");
const InventoryList = lazy(loadInventoryList);
const loadInventoryDetail = () => import("@/pages/inventory/detail");
const InventoryDetail = lazy(loadInventoryDetail);
const ProductImporterPage = lazy(() => import("@/pages/inventory/product-importer"));
const DraftFormPage = lazy(() => import("@/pages/inventory/draft-form"));
const RepairCentre = lazy(() => import("@/pages/inventory/repair-centre"));
const CatalogueQualityPage = lazy(() => import("@/pages/inventory/catalogue-quality"));
const InventoryLiveView = lazy(() => import("@/pages/inventory/live-view"));
const ImageStudioHome = lazy(() => import("@/pages/image-studio/index"));
const ImageStudioEditor = lazy(() => import("@/pages/image-studio/editor"));
const ImageStudioScenes = lazy(() => import("@/pages/image-studio/scenes"));
const MarketplacePresetsPage = lazy(() => import("@/pages/image-studio/marketplace-presets"));
const CompositePresetsPage = lazy(() => import("@/pages/image-studio/composite-presets"));
const FilterEffectsPage = lazy(() => import("@/pages/image-studio/filter-effects"));
const SceneEditorPage = lazy(() => import("@/pages/image-studio/scene-editor"));
const ValuationMigrationPage = lazy(() => import("@/pages/inventory/valuation-migration"));
const ValuationRulesPage = lazy(() => import("@/pages/inventory/valuation-rules"));
const ValuationSettingsPage = lazy(() => import("@/pages/settings/valuation"));
const CustomerPortfoliosIndex = lazy(() => import("@/pages/customer-portfolios/index"));
const CustomerPortfolioDetail = lazy(() => import("@/pages/customer-portfolios/detail"));
const CustomerPortfolioCustomer = lazy(() => import("@/pages/customer-portfolios/customer"));
const CustomerPortfolioAnalytics = lazy(() => import("@/pages/customer-portfolios/analytics"));
const CustomerPortfolioSettings = lazy(() => import("@/pages/customer-portfolios/settings"));
const CustomerPortfolioBranding = lazy(() => import("@/pages/customer-portfolios/branding"));
const CustomerPortfolioProducts = lazy(() => import("@/pages/customer-portfolios/products"));
const InventoryCostingSettingsPage = lazy(() => import("@/pages/settings/inventory-costing"));
const MessagesPage = lazy(() => import("@/pages/messages"));
const WhatsappInboxPage = lazy(() => import("@/pages/whatsapp-inbox"));
const WhatsappAutomationsPage = lazy(() => import("@/pages/whatsapp-inbox").then((module) => ({ default: module.WhatsappAutomationsPage })));
const WhatsappTrackingPage = lazy(() => import("@/pages/whatsapp-tracking"));
const WhatsappInboxSettingsPage = lazy(() => import("@/pages/whatsapp-inbox").then((module) => ({ default: module.WhatsappInboxSettingsPage })));
const WhatsappInboxAnalyticsPage = lazy(() => import("@/pages/whatsapp-inbox").then((module) => ({ default: module.WhatsappInboxAnalyticsPage })));
const SuppliersList = lazy(() => import("@/pages/suppliers/index"));
const SupplierDetail = lazy(() => import("@/pages/suppliers/detail"));
const PurchaseOrdersList = lazy(() => import("@/pages/purchase-orders/index"));
const PurchaseOrderDetail = lazy(() => import("@/pages/purchase-orders/detail"));
const NewPurchaseOrder = lazy(() => import("@/pages/purchase-orders/new"));
const ShipmentGroupsList = lazy(() => import("@/pages/purchase-orders/shipment-groups"));
const ShipmentGroupDetail = lazy(() => import("@/pages/purchase-orders/shipment-group-detail"));
const CustomersList = lazy(() => import("@/pages/customers/index"));
const CustomerDetail = lazy(() => import("@/pages/customers/detail"));
const MergeCustomers = lazy(() => import("@/pages/customers/merge"));
const NewCustomer = lazy(() => import("@/pages/customers/new"));
const EditCustomer = lazy(() => import("@/pages/customers/edit"));
const CompaniesList = lazy(() => import("@/pages/companies/index"));
const CompanyDetail = lazy(() => import("@/pages/companies/detail"));
const NewCompany = lazy(() => import("@/pages/companies/new"));
const EditCompany = lazy(() => import("@/pages/companies/edit"));
const loadSalesList = () => import("@/pages/sales/index");
const SalesList = lazy(loadSalesList);
const SaleDetail = lazy(() => import("@/pages/sales/detail"));
const NewSale = lazy(() => import("@/pages/sales/new"));
const SalesImport = lazy(() => import("@/pages/sales/import"));
const WhatnotFeesAdjuster = lazy(() => import("@/pages/sales/whatnot-fees"));
const VatDuePage = lazy(() => import("@/pages/vat-due/index"));
const ImportIssuesPage = lazy(() => import("@/pages/imports/issues"));
const InvoicesList = lazy(() => import("@/pages/invoices/index"));
const InvoiceDetail = lazy(() => import("@/pages/invoices/detail"));
const NewInvoice = lazy(() => import("@/pages/invoices/new"));
const EditInvoiceDraft = lazy(() => import("@/pages/invoices/edit"));
const Channels = lazy(() => import("@/pages/channels"));

const IntegrationsIndex = lazy(() => import("@/pages/integrations/index"));
const QuickBooksSyncLog = lazy(() => import("@/pages/quickbooks"));
const XeroSyncLog = lazy(() => import("@/pages/xero"));
const CategoriesPage = lazy(() => import("@/pages/categories"));
const TitleMappingsPage = lazy(() => import("@/pages/title-mappings/index"));
const TikTokMappingsPage = lazy(() => import("@/pages/tiktok-mappings/index"));
const TikTokShopDashboardPage = lazy(() => import("@/pages/tiktok-shop/dashboard"));
const TikTokShopProductsPage = lazy(() => import("@/pages/tiktok-shop/products"));
const TikTokShopOrdersPage = lazy(() => import("@/pages/tiktok-shop/orders"));
const TikTokShopMessagesPage = lazy(() => import("@/pages/tiktok-shop/messages"));
const TikTokShopSyncCentrePage = lazy(() => import("@/pages/tiktok-shop/sync-centre"));
const TikTokShopDeveloperPage = lazy(() => import("@/pages/tiktok-shop/developer"));
const TikTokShopConnectionsPage = lazy(() => import("@/pages/tiktok-shop/connections"));
const TikTokShopSettingsPage = lazy(() => import("@/pages/tiktok-shop/settings"));
const TikTokShopAuditPage = lazy(() => import("@/pages/tiktok-shop/audit"));
const TikTokShopPushListingsPage = lazy(() => import("@/pages/tiktok-shop/push-listings"));
const ForecastingPage = lazy(() => import("@/pages/forecasting/index"));
const IntelligencePage = lazy(() => import("@/pages/intelligence/index"));
const PurchasingPage = lazy(() => import("@/pages/purchasing/index"));
const AutomationsPage = lazy(() => import("@/pages/automations/index"));
const AutomationBuilderPage = lazy(() => import("@/pages/automations/builder"));
const AutomationLogsPage = lazy(() => import("@/pages/automations/logs"));
const PricingPage = lazy(() => import("@/pages/pricing/index"));
const LiveStockExportUsersPage = lazy(() => import("@/pages/admin/live-stock-export-users"));

const PricingRulesPage = lazy(() => import("@/pages/pricing/rules"));
const AiAgentsPage = lazy(() => import("@/pages/ai-agents/index"));

const LegacySystemHealthPage = lazy(() => import("@/pages/admin/system-health"));
const WorkerDashboardPage = lazy(() => import("@/pages/admin/worker-dashboard"));
const BackgroundJobsAdminPage = lazy(() => import("@/pages/admin/background-jobs"));
const PerformanceCentrePage = lazy(() => import("@/pages/admin/performance-centre"));
const BackgroundActivityPage = lazy(() => import("@/pages/settings/background-activity"));
const Packing = lazy(() => import("@/pages/packing"));
const PackingJobDetail = lazy(() => import("@/pages/packing/job-detail"));
const PickListIndex = lazy(() => import("@/pages/packing/pick-list/index"));
const PickListDetail = lazy(() => import("@/pages/packing/pick-list/detail"));
const PickListSlips = lazy(() => import("@/pages/packing/pick-list/slips"));
const PdfSplitter = lazy(() => import("@/pages/packing/pdf-splitter"));
const PrintPackStation = lazy(() => import("@/pages/print-pack"));
const Expenses = lazy(() => import("@/pages/expenses"));
const Stationery = lazy(() => import("@/pages/stationery"));
const Reports = lazy(() => import("@/pages/reports"));
const ScheduledCreditReporting = lazy(() => import("@/components/reports/scheduled-credit-reporting"));
const HazelReportsPage = lazy(() => import("@/pages/hazel-reports"));
const CustomerInsights = lazy(() => import("@/pages/customer-insights"));
const MergeReport = lazy(() => import("@/pages/reports/merges"));
const AdvancedAnalytics = lazy(() => import("@/pages/advanced-analytics"));
const ProfitLoss = lazy(() => import("@/pages/profit-loss"));
const SalesPerformance = lazy(() => import("@/pages/sales-performance"));
const ProductPerformance = lazy(() => import("@/pages/product-performance"));
const DemandIntelligencePage = lazy(() => import("@/pages/demand-intelligence"));
const Settings = lazy(() => import("@/pages/settings"));
const BrandCustomisation = lazy(() => import("@/pages/admin/brand-customisation"));
function DashboardBrandCustomisation() {
  return <BrandCustomisation section="dashboard" />;
}

function ReportsRoute() {
  return (
    <ReportsRouteGate>
      <Reports />
    </ReportsRouteGate>
  );
}
function InvoiceBrandCustomisation() {
  return <BrandCustomisation section="invoices" />;
}
const AdminHeroShotSettings = lazy(() => import("@/pages/admin/hero-shot-settings"));
const NotificationPreferences = lazy(() => import("@/pages/settings/notifications-preferences"));
const NotificationDefaults = lazy(() => import("@/pages/settings/notifications-defaults"));
const NotificationThresholds = lazy(() => import("@/pages/settings/notifications-thresholds"));
const AiProductCopySettings = lazy(() => import("@/pages/settings/ai-product-copy"));
const EmailCenter = lazy(() => import("@/pages/email-center"));
const NotificationsPage = lazy(() => import("@/pages/notifications"));
const EmailCampaigns = lazy(() => import("@/pages/email-campaigns"));
const EmailCampaignAnalytics = lazy(() => import("@/pages/email-campaigns/analytics"));
const UserPermissionsPage = lazy(() => import("@/pages/admin/user-permissions"));
const ReconciliationPage = lazy(() => import("@/pages/admin/reconciliation"));
const SidebarManagerPage = lazy(() => import("@/pages/admin/sidebar-manager"));
const SidebarItemFormPage = lazy(() => import("@/pages/admin/sidebar-item-form"));
const FlagsAdminPage = lazy(() => import("@/pages/admin/flags"));
const AiToolsAdminPage = lazy(() => import("@/pages/admin/ai-tools"));
const WorkflowsAdminPage = lazy(() => import("@/pages/admin/workflows"));
const CustomFieldsAdminPage = lazy(() => import("@/pages/admin/custom-fields"));
const ActivityLog = lazy(() => import("@/pages/activity-log"));
const AuditLogBrowser = lazy(() => import("@/pages/admin/audit-log"));
const EbaySyncIssuesPage = lazy(() => import("@/pages/admin/ebay-sync-issues"));
const WhatnotStockIssuesPage = lazy(() => import("@/pages/admin/whatnot-stock-issues"));
const EbaySyncRunsPage = lazy(() => import("@/pages/admin/ebay-sync-runs"));
const SalesReallocationPage = lazy(() => import("@/pages/admin/sales-reallocation"));
const ConsignmentRebalancePage = lazy(() => import("@/pages/admin/consignment-rebalance"));
const ShopifyStubReconcilePage = lazy(() => import("@/pages/admin/shopify-stub-reconcile"));
const TransformationsAdminPage = lazy(() => import("@/pages/admin/transformations"));
const ImageTemplatesPage = lazy(() => import("@/pages/admin/image-templates"));

const LegacyTenantsAdminPage = lazy(() => import("@/pages/admin/tenants"));
// Admin Control Centre workspaces stay independently lazy so entering one
// workspace never imports or requests the other five.
const AdminTenantsWorkspace = lazy(() => import("@/pages/admin/workspaces/tenants"));
const AdminTenantDetailWorkspace = lazy(() => import("@/pages/admin/workspaces/tenant-detail"));
const AdminUsersWorkspace = lazy(() => import("@/pages/admin/workspaces/users"));
const AdminUserDetailWorkspace = lazy(() => import("@/pages/admin/workspaces/user-detail"));
const AdminRolesWorkspace = lazy(() => import("@/pages/admin/workspaces/roles"));
const AdminSettingsWorkspace = lazy(() => import("@/pages/admin/workspaces/settings"));
const AdminBillingWorkspace = lazy(() => import("@/pages/admin/workspaces/billing"));
const AdminHealthWorkspace = lazy(() => import("@/pages/admin/workspaces/health"));
const PlatformFeatureManagement = lazy(
  () => import("@/pages/platform/feature-management"),
);
const PlatformWebsiteBuilder = lazy(
  () => import("@/pages/platform/website-builder"),
);
const PublicSiteContentStudio = lazy(
  () => import("@/pages/public-site"),
);
const PlatformPlansPricing = lazy(
  () => import("@/pages/platform/plans-pricing"),
);
const PlatformMemberships = lazy(() => import("@/pages/platform/memberships"));
const TenantsDashboard = lazy(() => import("@/pages/tenants/dashboard"));
const TenantDetail = lazy(() => import("@/pages/tenants/tenant-detail"));
const ServicePeaks = lazy(() => import("@/pages/tenants/service-peaks"));
const FeatureUsage = lazy(() => import("@/pages/tenants/feature-usage"));
const PlatformBilling = lazy(() => import("@/pages/platform/billing"));
const PlatformEnquiries = lazy(() => import("@/pages/platform/enquiries"));
const PlatformSupport = lazy(() => import("@/pages/platform/support"));
const PlatformSecrets = lazy(() => import("@/pages/platform/secrets"));
const PlatformAiPromptTemplates = lazy(() => import("@/pages/platform/ai-prompt-templates"));
const PlatformMarketData = lazy(() => import("@/pages/platform/market-data"));

const PlatformHelpAnalytics = lazy(() => import("@/pages/platform/help-analytics"));
const IncidentCenter = lazy(() => import("@/pages/admin/losses"));
const EbayFeedbackPage = lazy(() => import("@/pages/marketplaces/ebay-feedback"));
const SocialHub = lazy(() => import("@/pages/social"));
const SocialCompose = lazy(() => import("@/pages/social/compose"));
const SocialCalendar = lazy(() => import("@/pages/social/calendar"));
const SocialScheduled = lazy(() => import("@/pages/social/scheduled"));
const SocialDrafts = lazy(() => import("@/pages/social/drafts"));
const SocialPublished = lazy(() => import("@/pages/social/published"));
const SocialApprovals = lazy(() => import("@/pages/social/approvals"));
const SocialSettings = lazy(() => import("@/pages/social/settings"));
const SocialAudit = lazy(() => import("@/pages/social/audit"));
const SocialMediaLibrary = lazy(() => import("@/pages/social/media-library"));
const SocialGallery = lazy(() => import("@/pages/social/gallery"));
const SocialBrandKit = lazy(() => import("@/pages/social/brand-kit"));
const SocialTemplates = lazy(() => import("@/pages/social/templates"));
const SocialStudio = lazy(() => import("@/pages/social/studio"));
const SocialCampaigns = lazy(() => import("@/pages/social/campaigns"));
const SocialAnalytics = lazy(() => import("@/pages/social/analytics"));
const SocialInbox = lazy(() => import("@/pages/social/inbox"));
const SocialIntegrations = lazy(() => import("@/pages/social/integrations"));
const SocialHistory = lazy(() => import("@/pages/social/history"));
const DropshippingDashboard = lazy(() => import("@/pages/dropshipping/index"));
const DropshippingCustomers = lazy(() => import("@/pages/dropshipping/customers"));
const DropshippingCustomerDetail = lazy(() => import("@/pages/dropshipping/customer-detail"));
const ConsignmentDashboard = lazy(() => import("@/pages/consignment/dashboard"));
const ConsignmentWorkspace = lazy(() => import("@/pages/consignment/workspace"));
const ConsignmentStock = lazy(() => import("@/pages/consignment/stock"));
const ConsignmentSuppliers = lazy(() => import("@/pages/consignment/suppliers"));
const ConsignmentSupplierDetail = lazy(() => import("@/pages/consignment/supplier-detail"));
const ConsignmentSkuPayoutDetail = lazy(() => import("@/pages/consignment/sku-payout-detail"));
const ConsignmentSyncPartnerships = lazy(() => import("@/pages/consignment-sync/index"));
const ConsignmentSyncIncoming = lazy(() => import("@/pages/consignment-sync/incoming"));
const ConsignmentSyncOutgoing = lazy(() => import("@/pages/consignment-sync/outgoing"));
const HelpPage = lazy(() => import("@/pages/help"));

registerRoutePreload("/inventory", loadInventoryList);
registerRoutePreload("/inventory/:id", loadInventoryDetail);
registerRoutePreload("/sales", loadSalesList);

const MarketsDashboard = lazy(() => import("@/pages/markets/index"));
const AccountPage = lazy(() => import("@/pages/account"));
const ConsignmentPayouts = lazy(() => import("@/pages/consignment/payouts"));
const ConsignmentPayoutDetail = lazy(() => import("@/pages/consignment/payout-detail"));
const ConsignmentStatementDetail = lazy(() => import("@/pages/consignment/statement-detail"));
const ConsignmentBatches = lazy(() => import("@/pages/consignment/batches"));
const ConsignmentBatchDetail = lazy(() => import("@/pages/consignment/batch-detail"));
const AdjustmentsList = lazy(() => import("@/pages/adjustments/index"));
const MyTasks = lazy(() => import("@/pages/tasks/index"));
const TeamTasks = lazy(() => import("@/pages/tasks/team"));
const CompletedTasks = lazy(() => import("@/pages/tasks/completed"));
const TaskReminders = lazy(() => import("@/pages/tasks/reminders"));
const TaskCalendar = lazy(() => import("@/pages/tasks/calendar"));
const TaskSettings = lazy(() => import("@/pages/tasks/settings"));
const TaskProjects = lazy(() => import("@/pages/tasks/projects/index"));
const TaskProjectDetail = lazy(() => import("@/pages/tasks/projects/detail"));
const TaskTemplates = lazy(() => import("@/pages/tasks/templates"));
const TaskDetail = lazy(() => import("@/pages/tasks/detail"));

const CustomerSupportWorkspace = lazy(() =>
  import("@/pages/customers/CustomerSellingOperations").then((module) => ({
    default: module.CustomerSupportWorkspace,
  })),
);

// Scrap module (Task #99) — feature-gated add-on, isolated from Sales/Stock.
const ScrapDashboard = lazy(() => import("@/pages/scrap/dashboard"));
const ScrapPurchasesList = lazy(() => import("@/pages/scrap/purchases/list"));
const ScrapPurchaseNew = lazy(() => import("@/pages/scrap/purchases/new"));
const ScrapPurchaseDetail = lazy(() => import("@/pages/scrap/purchases/detail"));
const ScrapInventory = lazy(() => import("@/pages/scrap/inventory"));
const ScrapRefiningList = lazy(() => import("@/pages/scrap/refining/list"));
const ScrapRefiningDetail = lazy(() => import("@/pages/scrap/refining/detail"));
const ScrapRefiningResults = lazy(() => import("@/pages/scrap/refining/results"));
const ScrapReports = lazy(() => import("@/pages/scrap/reports"));
const ScrapSettings = lazy(() => import("@/pages/scrap/settings"));
const SellToUsWorkspace = lazy(() =>
  import("@/pages/customers/CustomerSellingOperations").then((module) => ({
    default: module.SellToUsWorkspace,
  })),
);

// HR module (Task #189) — feature-gated add-on, tenant-isolated, hidden when
// `hr_module_enabled` is off. Gates via the section-based `featureGated` helper
// since "hr" is a registered SectionId mapped to the `hr.view` permission.
const HrOverview = lazy(() => import("@/pages/hr/overview"));
const HrStaffList = lazy(() => import("@/pages/hr/staff/index"));
const HrPerformance = lazy(() => import("@/pages/hr/performance"));
const HrPerformanceStaff = lazy(() => import("@/pages/hr/performance-staff"));
const HrRota = lazy(() => import("@/pages/hr/rota"));
const HrPayments = lazy(() => import("@/pages/hr/payments"));
const HrStaffDetail = lazy(() => import("@/pages/hr/staff/detail"));
const HrMe = lazy(() => import("@/pages/hr/me"));
const HrTimeOff = lazy(() => import("@/pages/hr/time-off"));
const HrChangeRequests = lazy(() => import("@/pages/hr/change-requests"));
const HrOnboardingTemplates = lazy(() => import("@/pages/hr/onboarding-templates"));
const HrSettings = lazy(() => import("@/pages/hr/settings"));
// Affiliate Payout Management (Task #1443) — off-contract module gated on the
// `affiliate_payouts` feature key via the section-based `featureGated` helper.
const AffiliatesDashboard = lazy(() => import("@/pages/affiliates/index"));
const AffiliatesList = lazy(() => import("@/pages/affiliates/list"));
const AffiliateDetail = lazy(() => import("@/pages/affiliates/detail"));
const AffiliateRules = lazy(() => import("@/pages/affiliates/rules"));
const AffiliatePayouts = lazy(() => import("@/pages/affiliates/payouts"));
const AffiliateBatches = lazy(() => import("@/pages/affiliates/batches"));
const AffiliateStatements = lazy(() => import("@/pages/affiliates/statements"));
const AffiliateReports = lazy(() => import("@/pages/affiliates/reports"));
const AffiliateSettings = lazy(() => import("@/pages/affiliates/settings"));
// Consignment Profit Split Management (Task #1444) — off-contract module gated
// on the `consignment_profit_splits` feature key via `featureGated`.
const ConsignmentSplitsDashboard = lazy(() => import("@/pages/consignment-splits/index"));
const ConsignmentSplitRules = lazy(() => import("@/pages/consignment-splits/rules"));
const ConsignmentSplitCalculations = lazy(() => import("@/pages/consignment-splits/splits"));
const ConsignmentSplitAdjustments = lazy(() => import("@/pages/consignment-splits/adjustments"));
const ConsignmentSettlements = lazy(() => import("@/pages/consignment-splits/settlements"));
const ConsignmentSettlementDetail = lazy(() => import("@/pages/consignment-splits/settlement-detail"));
const ConsignmentSplitReports = lazy(() => import("@/pages/consignment-splits/reports"));
const ConsignmentSplitSettings = lazy(() => import("@/pages/consignment-splits/settings"));
const ConsignmentSplitAudit = lazy(() => import("@/pages/consignment-splits/audit"));
const HrAudit = lazy(() => import("@/pages/hr/audit"));

// Returns module — hidden unless the tenant has `returns_module_enabled`.
// Gated via the section-based `featureGated` helper since "returns" is a
// registered SectionId mapped to the `returns.view` permission.
const ReturnsOverview = lazy(() => import("@/pages/returns/overview"));
const ReturnsList = lazy(() => import("@/pages/returns/index"));
const ReturnsDetail = lazy(() => import("@/pages/returns/detail"));
const ReturnsSettings = lazy(() => import("@/pages/returns/settings"));
const ReturnsReports = lazy(() => import("@/pages/returns/reports"));

// Pre-Orders module (Task #2058) — hidden unless the tenant has
// `pre_orders_enabled`. The wrappers below mirror the backend's existing
// feature + permission contract; Commerce adapters deliberately reuse this
// same workspace and API rather than introducing a second allocator.
const PreOrderBatchList = lazy(() => import("@/pages/pre-orders/index"));

const OperationsInbox = lazy(() => import("@/pages/exceptions/index"));
// Customer Commerce Portal (Task #2242). The pre-orders/pre-sales screens are
// adapters over the canonical workspace and keep the same server-owned gates.
const CommerceHome = lazy(() => import("@/pages/commerce/index"));
const CommerceDashboard = lazy(() => import("@/pages/commerce/dashboard"));
const CommerceProducts = lazy(() => import("@/pages/commerce/products"));
const CommerceCategories = lazy(() => import("@/pages/commerce/categories"));
const CommerceCollections = lazy(() => import("@/pages/commerce/collections"));
const CommerceBoxes = lazy(() => import("@/pages/commerce/boxes"));
const CommerceCheckouts = lazy(() => import("@/pages/commerce/checkouts"));
const CommerceAwaitingPayment = lazy(() => import("@/pages/commerce/awaiting-payment"));
const CommercePreOrders = lazy(() => import("@/pages/commerce/pre-orders"));
const CommercePreSales = lazy(() => import("@/pages/commerce/pre-sales"));
const CommerceCustomers = lazy(() => import("@/pages/commerce/customers"));
const CommerceCustomerGroups = lazy(() => import("@/pages/commerce/customer-groups"));
const CommercePricing = lazy(() => import("@/pages/commerce/pricing"));
const CommerceQuotes = lazy(() => import("@/pages/commerce/quotes"));
const CommercePayments = lazy(() => import("@/pages/commerce/payments"));
const CommercePostage = lazy(() => import("@/pages/commerce/postage"));
const CommercePortalEditor = lazy(() => import("@/pages/commerce/portal-editor"));
const CommerceAnalytics = lazy(() => import("@/pages/commerce/analytics"));
const CommerceSettings = lazy(() => import("@/pages/commerce/settings"));
const CommerceHelp = lazy(() => import("@/pages/commerce/help"));
const PreOrderBatchDetail = lazy(() => import("@/pages/pre-orders/detail"));

// Evidence Centre — hidden unless the tenant has `evidence_centre_enabled`.
// Gated via the section-based `featureGated` helper since "evidence" is a
// registered SectionId mapped to the `evidence.view` permission.
const EvidenceCentre = lazy(() => import("@/pages/evidence/index"));

// Document Generation module — hidden unless the tenant has
// `document_generation_enabled`. Gated via the section-based `featureGated`
// helper since "documents" is a registered SectionId mapped to the
// `documents.view` permission.
const DocumentsOverview = lazy(() => import("@/pages/documents/index"));
const DocumentTemplates = lazy(() => import("@/pages/documents/templates"));
const GeneratedDocuments = lazy(() => import("@/pages/documents/generated"));
const DocumentsLog = lazy(() => import("@/pages/documents/settings"));
// Asset Tracking module — hidden unless the tenant has `asset_tracking_enabled`.
// Gated via the section-based `featureGated` helper ("asset_tracking" is a
// registered SectionId mapped to the `asset.view` permission).
const AssetsOverview = lazy(() => import("@/pages/assets/overview"));
const AssetsList = lazy(() => import("@/pages/assets/index"));
const AssetsDetail = lazy(() => import("@/pages/assets/detail"));

// Warehouse (WMS) module — hidden unless the tenant has `wms_enabled`.
// Gated via the section-based `featureGated` helper ("warehouse" is a
// registered SectionId mapped to the `warehouse.view` permission).
const WarehouseOverview = lazy(() => import("@/pages/warehouse/overview"));
const WarehouseLocations = lazy(() => import("@/pages/warehouse/locations"));
// Compliance Vault module — hidden unless the tenant has
// `compliance_vault_enabled`. Gated via the section-based `featureGated` helper
// ("compliance" is a UI section in users.ts), mirroring Asset Tracking.
const ComplianceOverview = lazy(() => import("@/pages/compliance/overview"));
const ComplianceProfiles = lazy(() => import("@/pages/compliance/index"));
const ComplianceProfileDetail = lazy(() => import("@/pages/compliance/detail"));
const ComplianceTasks = lazy(() => import("@/pages/compliance/tasks"));

// Postage & Shipping module pages. All gated behind the
// `postage_module_enabled` feature key and the "postage" SectionId.
const PostageOverview = lazy(() => import("@/pages/postage/index"));
const PostageShipments = lazy(() => import("@/pages/postage/shipments"));
const PostageCombined = lazy(() => import("@/pages/postage/combined"));
const PostageBatch = lazy(() => import("@/pages/postage/batch"));
const PostageCouriers = lazy(() => import("@/pages/postage/couriers"));
const PostageReports = lazy(() => import("@/pages/postage/reports"));
const PostageSettings = lazy(() => import("@/pages/postage/settings"));
const PostageAudit = lazy(() => import("@/pages/postage/audit"));
const PerksIndex = lazy(() => import("@/pages/platforms/perks"));
const PerksStreamingTools = lazy(() => import("@/pages/platforms/perks/streaming-tools"));
const PerksOverlayDesigner = lazy(() => import("@/pages/platforms/perks/overlay-designer"));
const PerksProductQueue = lazy(() => import("@/pages/platforms/perks/product-queue"));
const PerksLiveEvent = lazy(() => import("@/pages/platforms/perks/live-event"));

const MarketOverlayStudio = lazy(() => import("@/pages/markets/overlay-studio"));
const AccountPortal = lazy(() => import("@/pages/account-portal"));
const AccountFeatures = lazy(() => import("@/pages/account-portal/features"));
const AccountPlan = lazy(() => import("@/pages/account-portal/plan"));
const AccountUsage = lazy(() => import("@/pages/account-portal/usage"));
const PortfolioDomains = lazy(() => import("@/pages/account-portal/portfolio-domains"));
const PublicMarketsBuilder = lazy(() => import("@/pages/customer-portfolios/public-markets"));
const AccountRequests = lazy(() => import("@/pages/account-portal/requests"));
const AccountRequestNew = lazy(() => import("@/pages/account-portal/request-new"));
const AccountRequestDetail = lazy(() => import("@/pages/account-portal/request-detail"));
const AccountHelp = lazy(() => import("@/pages/account-portal/help"));
const AccountBilling = lazy(() => import("@/pages/account-portal/billing"));
const AccountInvoices = lazy(() => import("@/pages/account-portal/invoices"));
const AccountPayments = lazy(() => import("@/pages/account-portal/payments"));
const AccountUsers = lazy(() => import("@/pages/account-portal/users"));
const AccountBusiness = lazy(() => import("@/pages/account-portal/business"));
const AccountPaypalReturn = lazy(() => import("@/pages/account-portal/paypal-return"));
const AccountSecurity = lazy(() => import("@/pages/account-portal/security"));
const AccountSupport = lazy(() => import("@/pages/account-portal/support"));
const AccountNotifications = lazy(() => import("@/pages/account-portal/notifications"));
const AccountTrial = lazy(() => import("@/pages/account-portal/trial"));

// Location-aware skeleton fallback shown while a lazy route chunk downloads.
// The MainLayout sidebar/header stays mounted around it so navigation feels
// instant. Shape matches the expected page type:
//   • detail pages → VCDetailSkeleton
//   • card/grid pages → VCCardGridSkeleton
//   • everything else → VCTableSkeleton (most common)
function RouteFallback() {
  const [loc] = useLocation();
  // Detail pages — URL ends with a UUID/numeric ID (not a known list suffix)
  const isDetail =
    /\/[0-9a-f-]{8,}$/.test(loc) ||
    /\/\d+$/.test(loc) ||
    /\/(detail|edit|new|account|brand-options|invoice-template|notifications-preferences|notifications-defaults|notifications-thresholds|ai-product-copy)/.test(loc);
  // Card/grid layouts
  const isCardGrid =
    /\/(dashboard|reports\/advanced|sales-performance|profit-loss|channels|integrations|packing(?!\/jobs))/.test(loc);

  if (isDetail) return <VCDetailSkeleton aria-live="polite" />;
  if (isCardGrid) return <VCCardGridSkeleton aria-live="polite" />;
  return <VCTableSkeleton aria-live="polite" />;
}

/** Records browser timing for the first usable shell without blocking rendering. */
function AppPerformanceInstrumentation({ fallbackDelayMs }: { fallbackDelayMs?: number }) {
  const [location] = useLocation();
  const previousLocation = useRef(location);
  useAppPerformanceInstrumentation(fallbackDelayMs);
  // Reset during the parent render, before the newly-mounted route's effects
  // can mark cached data ready. A normal effect runs after child effects, which
  // let a warm dashboard mark get discarded immediately afterward.
  if (previousLocation.current !== location) {
    previousLocation.current = location;
    beginRouteNavigation(location);
  }
  return null;
}

// Global query defaults are tuned to keep the app *quiet* in the background.
// Earlier versions used the React Query defaults (staleTime: 0,
// refetchOnWindowFocus: true), which combined with Clerk's ~60s session JWT
// rotation caused every open page to silently refetch its data every minute.
// To the user that looked and felt like the whole app was "auto-refreshing"
// on a 30-60s timer — losing scroll position, briefly blanking lists, and in
// the worst case dropping in-flight form state when an unmounted parent
// briefly re-rendered. Concretely:
//   - staleTime: 30s — avoids a thundering herd of refetches every time a
//     component remounts within half a minute (common during tab switches).
//   - gcTime: 5min — keeps cached data around longer so switching back to a
//     tab is instant rather than triggering a fresh fetch.
//   - refetchOnWindowFocus: false — the single biggest cause of the
//     perceived "auto-refresh" loop. Users alt-tab between Replit chat and
//     the preview iframe constantly; we never want to wipe their work just
//     because focus returned.
//   - refetchOnReconnect: "always" — when the network actually comes back
//     after dropping, we *do* want a refresh.
//   - retry: 1 — fail fast on real errors instead of hammering the server.
// Note: components that genuinely want polling (e.g. the Shopify sync
// progress card) opt in explicitly with `refetchInterval` on their own
// useQuery call. They are unaffected by these defaults.
const queryClient = new QueryClient({
  // Per-user rate-limited endpoints (see lib/rateLimit.ts on the server)
  // return HTTP 429 with a `Retry-After` header and `{ retryAfterSec }`
  // body. The QueryCache `onError` hook surfaces a friendly, deduped
  // toast naming the throttled area + a live countdown, and pauses any
  // polling `refetchInterval` until the cooldown elapses so we stop
  // hammering the throttled endpoint.
  queryCache: new QueryCache({
    onError: (error, query) => {
      handleQueryErrorForThrottle(error, query);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      // Existing successful data is retained automatically while a background
      // refetch is in flight. Do not set a global placeholderData function:
      // parameterised observers can change keys to a different resource shape,
      // where carrying the previous value would briefly show misleading data.
      // Use keep-previous-data locally only for deliberately paginated views.
      // Polling is explicitly paused in hidden tabs and resumes when visible.
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: "always",
      // Never retry 429s — the server is explicitly asking us to back
      // off. Retrying immediately would burn the operator's window AND
      // make the perceived cooldown longer than `Retry-After`.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status === 429) return false;
        return failureCount < 1;
      },
    },
    mutations: {
      retry: 0,
    },
  },
});

function WhatnotFeeInvalidationSync() {
  const client = useQueryClient();
  useEffect(
    () => subscribeToWhatnotFeeUpdates(() => {
      void client.invalidateQueries({ predicate: isWhatnotFeeAffectedQuery });
    }),
    [client],
  );
  return null;
}

// Route-level throttle handling (Task #1917): let a 429 on one query pause
// every sibling query polling the same route, and short-circuit new GETs to
// a route in an active cooldown client-side (synthetic 429, no network hit).
setThrottleQueryCache(queryClient.getQueryCache());
setFetchGate((url) => throttleFetchGate(url));

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
// Clerk frontend-API proxy is intentionally NOT used. Production runs on the
// operator's OWN external Clerk with a CNAME custom domain (clerk.metalui.co.uk,
// encoded in the pk_live key), and Clerk's proxy mode and CNAME custom-domain
// mode are mutually exclusive. The old `/api/__clerk` proxy forwarded to the
// shared `frontend-api.clerk.dev` host, which cannot attribute the new instance
// and returns `host_invalid`, hanging the app on "Loading…". We therefore let
// ClerkProvider talk DIRECTLY to the custom-domain FAPI derived from the
// publishable key. (Any leftover VITE_CLERK_PROXY_URL deployment env var is
// deliberately ignored.)
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

// Cache of section-gated component wrappers. Keyed by the underlying
// Component identity + section so the SAME wrapper is returned on every
// call. CRITICAL: without this cache, `gated()` returns a brand-new
// function on every render of <AuthorisedRoutes>, which React treats as
// a new component type and remounts the matched <Route>. Because
// AuthorisedRoutes re-renders whenever /api/me refetches (every ~60s),
// the entire current page (e.g. a half-filled multi-invoice purchase
// order) was being torn down and remounted on a 60-second timer, wiping
// any in-progress form state. The cache makes the wrapper identity
// stable, so React reuses the existing fiber and form state survives.
const _gatedCache = new WeakMap<
  React.ComponentType<unknown>,
  Map<SectionId, React.ComponentType<unknown>>
>();
function gated(section: SectionId, Component: React.ComponentType<unknown>) {
  let bySection = _gatedCache.get(Component);
  if (!bySection) {
    bySection = new Map();
    _gatedCache.set(Component, bySection);
  }
  const cached = bySection.get(section);
  if (cached) return cached;
  const Gated = function Gated(props: unknown) {
    const { hasSection, isLoading, error } = useMe();
    if (isLoading && !error) return null;
    if (!hasSection(section)) return <AccessDenied section={section} />;
    return <Component {...(props as object)} />;
  };
  Gated.displayName = `Gated(${Component.displayName ?? Component.name ?? "Anon"}, ${section})`;
  bySection.set(section, Gated);
  return Gated;
}

// Cross-tenant Admin Control Centre routes are governed by the
// platform-operator check, not the active tenant's `admin` section map. The
// workspace pages apply granular platform permissions to individual actions
// and authoritative management links.
const _platformOperatorCache = new WeakMap<
  React.ComponentType<unknown>,
  React.ComponentType<unknown>
>();
function platformOperatorGated(Component: React.ComponentType<unknown>) {
  const cached = _platformOperatorCache.get(Component);
  if (cached) return cached;
  const PlatformOperatorGated = function PlatformOperatorGated(props: unknown) {
    const { isPlatformOperator, isLoading } = useIsPlatformOperator();
    if (isLoading) return null;
    if (!isPlatformOperator) return <AccessDenied section="admin" />;
    return <Component {...(props as object)} />;
  };
  PlatformOperatorGated.displayName = `PlatformOperatorGated(${Component.displayName ?? Component.name ?? "Anon"})`;
  _platformOperatorCache.set(Component, PlatformOperatorGated);
  return PlatformOperatorGated;
}

function platformWorkspaceGated(
  workspace: import("@/pages/admin/workspaces/access").AdminWorkspace,
  Component: React.ComponentType<unknown>,
) {
  const PlatformWorkspaceGated = function PlatformWorkspaceGated(props: unknown) {
    const scoped = new URLSearchParams(useSearch()).has("tenantId");
    const { isPlatformOperator, isLoading: operatorLoading } = useIsPlatformOperator();
    const { permissions, isLoading: permissionsLoading } = useMyPlatformPermissions();
    if (operatorLoading || permissionsLoading) return null;
    if (
      !isPlatformOperator ||
      !canAccessAdminWorkspace(workspace, permissions)
    ) {
      return <AccessDenied section="admin" />;
    }
    if (scoped && !["tenants", "users", "roles"].includes(workspace)) {
      return <div className="p-6" role="alert">This workspace is global-only, not a tenant action. <a href="/admin/tenants">Return to tenant directory</a></div>;
    }
    return <Component {...(props as object)} />;
  };
  PlatformWorkspaceGated.displayName = `PlatformWorkspaceGated(${workspace}, ${Component.displayName ?? Component.name ?? "Anon"})`;
  return PlatformWorkspaceGated;
}

const _permissionGatedCache = new WeakMap<
  React.ComponentType<unknown>,
  Map<string, React.ComponentType<unknown>>
>();
function permissionGated(
  permission: Permission,
  Component: React.ComponentType<unknown>,
  section: SectionId = "admin",
) {
  let byPermission = _permissionGatedCache.get(Component);
  if (!byPermission) {
    byPermission = new Map();
    _permissionGatedCache.set(Component, byPermission);
  }
  const cacheKey = `${permission}::${section}`;
  const cached = byPermission.get(cacheKey);
  if (cached) return cached;
  const PermissionGated = function PermissionGated(props: unknown) {
    const { has, isLoading, error } = useMe();
    if (isLoading && !error) return null;
    if (!has(permission)) return <AccessDenied section={section} />;
    return <Component {...(props as object)} />;
  };
  byPermission.set(cacheKey, PermissionGated);
  return PermissionGated;
}

// Same stable-identity caching as `gated()`, but additionally gates on a
// MetalUI feature flag (Task #100). Used for add-on modules whose entire
// route should be unreachable unless the tenant has the feature granted —
// even by deep-linking past the (already feature-filtered) sidebar.
const _featureGatedCache = new WeakMap<
  React.ComponentType<unknown>,
  Map<string, React.ComponentType<unknown>>
>();
function featureGated(
  feature: FeatureKey,
  section: SectionId,
  Component: React.ComponentType<unknown>,
  /**
   * When provided, a disabled feature renders a friendly locked page with a
   * "Request access" CTA instead of the generic Access Denied screen. Only
   * use this for features that tenants can self-serve request (i.e. not
   * owner-only platform features).
   */
  featureLabel?: string,
) {
  let byKey = _featureGatedCache.get(Component);
  if (!byKey) {
    byKey = new Map();
    _featureGatedCache.set(Component, byKey);
  }
  const cacheKey = `${feature}::${section}::${featureLabel ?? ""}`;
  const cached = byKey.get(cacheKey);
  if (cached) return cached;
  const Gated = function FeatureGated(props: unknown) {
    const { hasSection, isLoading, error } = useMe();
    const { hasFeature, isLoading: featuresLoading } = useMyFeatures();
    if ((isLoading && !error) || featuresLoading) return null;
    if (!hasSection(section)) return <AccessDenied section={section} />;
    if (!hasFeature(feature)) {
      return featureLabel ? (
        <FeatureLockedPage featureKey={feature} label={featureLabel} />
      ) : (
        <AccessDenied section={section} />
      );
    }
    return <Component {...(props as object)} />;
  };
  Gated.displayName = `FeatureGated(${Component.displayName ?? Component.name ?? "Anon"}, ${feature})`;
  byKey.set(cacheKey, Gated);
  return Gated;
}

const DemandIntelligenceRoute = featureGated(
  "demand_intelligence_enabled",
  "dashboard",
  permissionGated("view_inventory", DemandIntelligencePage, "dashboard"),
);

const StaffPreOrderBatchList = featureGated(
  "pre_orders_enabled",
  "purchase_orders",
  permissionGated("pre_orders.view", PreOrderBatchList, "purchase_orders"),
);
const StaffPreOrderBatchDetail = featureGated(
  "pre_orders_enabled",
  "purchase_orders",
  permissionGated("pre_orders.view", PreOrderBatchDetail, "purchase_orders"),
);
const CommercePreOrderBatchList = featureGated(
  "customer_commerce_portal",
  "commerce",
  featureGated(
    "pre_orders_enabled",
    "purchase_orders",
    permissionGated("pre_orders.view", CommercePreOrders, "purchase_orders"),
  ),
);
const CommercePreSalesBatchList = featureGated(
  "customer_commerce_portal",
  "commerce",
  featureGated(
    "pre_orders_enabled",
    "purchase_orders",
    permissionGated("pre_orders.view", CommercePreSales, "purchase_orders"),
  ),
);
const CommercePreOrderBatchDetail = featureGated(
  "customer_commerce_portal",
  "commerce",
  featureGated(
    "pre_orders_enabled",
    "purchase_orders",
    permissionGated("pre_orders.view", PreOrderBatchDetail, "purchase_orders"),
  ),
);
const CommercePreSalesBatchDetail = featureGated(
  "customer_commerce_portal",
  "commerce",
  featureGated(
    "pre_orders_enabled",
    "purchase_orders",
    permissionGated("pre_orders.view", PreOrderBatchDetail, "purchase_orders"),
  ),
);

const CommercePostageGated = featureGated(
  "customer_commerce_portal",
  "commerce",
  featureGated(
    "postage_module_enabled",
    "postage",
    permissionGated("postage.view", CommercePostage, "postage"),
  ),
);
const _operationsGatedCache = new WeakMap<
  React.ComponentType<unknown>,
  Map<string, React.ComponentType<unknown>>
>();
function operationsGated(
  feature: FeatureKey,
  permission: Permission,
  Component: React.ComponentType<unknown>,
) {
  let byKey = _operationsGatedCache.get(Component);
  if (!byKey) {
    byKey = new Map();
    _operationsGatedCache.set(Component, byKey);
  }
  const cacheKey = `${feature}::${permission}`;
  const cached = byKey.get(cacheKey);
  if (cached) return cached;
  const Gated = function OperationsGated(props: unknown) {
    const { has, hasSection, isLoading, error } = useMe();
    const { hasFeature, isLoading: featuresLoading } = useMyFeatures();
    if ((isLoading && !error) || featuresLoading) return null;
    if (
      !hasSection("operations")
      || !has(permission)
      || !hasFeature("operations_workspace")
      || !hasFeature(feature)
    ) {
      return <AccessDenied section="operations" />;
    }
    return <Component {...(props as object)} />;
  };
  Gated.displayName = `OperationsGated(${Component.displayName ?? Component.name ?? "Anon"}, ${permission})`;
  byKey.set(cacheKey, Gated);
  return Gated;
}

// Scrap module gate (Task #99). The scrap module is NOT a UI "section" (it is
// deliberately kept out of UI_SECTIONS / SectionId), so it can't use the
// section-based `gated`/`featureGated` helpers. Instead it gates on the
// `scrap_module_enabled` feature flag AND the `view_scrap` permission — the
// same two checks the backend route enforces (404 when the feature is off,
// 403 without the permission). FE hiding is defence-in-depth only.
const _scrapGatedCache = new WeakMap<
  React.ComponentType<unknown>,
  React.ComponentType<unknown>
>();
function scrapGated(Component: React.ComponentType<unknown>) {
  const cached = _scrapGatedCache.get(Component);
  if (cached) return cached;
  const Gated = function ScrapGated(props: unknown) {
    const { has, isLoading, error } = useMe();
    const { hasFeature, isLoading: featuresLoading } = useMyFeatures();
    if ((isLoading && !error) || featuresLoading) return null;
    if (!hasFeature("scrap_module_enabled")) return <AccessDenied section="scrap" />;
    if (!has("view_scrap")) return <AccessDenied section="scrap" />;
    return <Component {...(props as object)} />;
  };
  Gated.displayName = `ScrapGated(${Component.displayName ?? Component.name ?? "Anon"})`;
  _scrapGatedCache.set(Component, Gated);
  return Gated;
}

const CustomerSupportRoute = permissionGated(
  "view_sales",
  CustomerSupportWorkspace,
  "customers",
);
const SellToUsRoute = scrapGated(SellToUsWorkspace);

// Forecasting & Demand Planning gate (Task #380). Like scrap, forecasting is
// NOT a UI "section" — it gates on the platform-owner-only
// `forecasting_demand_planning_enabled` feature flag AND the
// `forecasting.view` permission, the same two checks the backend route
// enforces (404/403 when off). FE hiding is defence-in-depth only.
const _forecastingGatedCache = new WeakMap<
  React.ComponentType<unknown>,
  React.ComponentType<unknown>
>();
function forecastingGated(Component: React.ComponentType<unknown>) {
  const cached = _forecastingGatedCache.get(Component);
  if (cached) return cached;
  const Gated = function ForecastingGated(props: unknown) {
    const { has, isLoading, error } = useMe();
    const { hasFeature, isLoading: featuresLoading } = useMyFeatures();
    if ((isLoading && !error) || featuresLoading) return null;
    if (!hasFeature("forecasting_demand_planning_enabled")) return <AccessDenied section="reports" />;
    if (!has("forecasting.view")) return <AccessDenied section="reports" />;
    return <Component {...(props as object)} />;
  };
  Gated.displayName = `ForecastingGated(${Component.displayName ?? Component.name ?? "Anon"})`;
  _forecastingGatedCache.set(Component, Gated);
  return Gated;
}

// Intelligence Centre gate (Task #917). Like forecasting, the Intelligence
// Centre is NOT a UI "section" — it gates on the platform-owner-only
// `intelligence_centre_enabled` feature flag AND the `intelligence.view`
// permission, the same two checks the backend route enforces (404/403 when
// off). FE hiding is defence-in-depth only.
const _catalogueIntelligenceGatedCache = new WeakMap<
  React.ComponentType<unknown>,
  React.ComponentType<unknown>
>();
function catalogueIntelligenceGated(Component: React.ComponentType<unknown>) {
  const cached = _catalogueIntelligenceGatedCache.get(Component);
  if (cached) return cached;
  const Gated = function CatalogueIntelligenceGated(props: unknown) {
    const { has, isLoading, error } = useMe();
    const { hasFeature, isLoading: featuresLoading } = useMyFeatures();
    if ((isLoading && !error) || featuresLoading) return null;
    if (
      !hasFeature("catalogue_intelligence_enabled" as any) ||
      !hasFeature("catalogue_intelligence_review" as any)
    ) {
      return <AccessDenied section="Catalogue Intelligence" />;
    }
    if (
      !has("catalogue_intelligence.view" as any) ||
      !has("catalogue_intelligence.review" as any)
    ) {
      return <AccessDenied section="Catalogue Intelligence" />;
    }
    return <Component {...(props as object)} />;
  };
  Gated.displayName = `CatalogueIntelligenceGated(${Component.displayName ?? Component.name ?? "Anon"})`;
  _catalogueIntelligenceGatedCache.set(Component, Gated);
  return Gated;
}

const _intelligenceGatedCache = new WeakMap<
  React.ComponentType<unknown>,
  React.ComponentType<unknown>
>();
function intelligenceGated(Component: React.ComponentType<unknown>) {
  const cached = _intelligenceGatedCache.get(Component);
  if (cached) return cached;
  const Gated = function IntelligenceGated(props: unknown) {
    const { has, isLoading, error } = useMe();
    const { hasFeature, isLoading: featuresLoading } = useMyFeatures();
    if ((isLoading && !error) || featuresLoading) return null;
    if (!hasFeature("intelligence_centre_enabled")) return <AccessDenied section="reports" />;
    if (!has("intelligence.view")) return <AccessDenied section="reports" />;
    return <Component {...(props as object)} />;
  };
  Gated.displayName = `IntelligenceGated(${Component.displayName ?? Component.name ?? "Anon"})`;
  _intelligenceGatedCache.set(Component, Gated);
  return Gated;
}

// Purchasing Automation gate (Task #924). Like forecasting, purchasing is NOT a
// UI "section" — it gates on the platform-owner-only
// `purchasing_automation_enabled` feature flag AND the `purchasing.view`
// permission, the same two checks the backend route enforces (404/403 when
// off). FE hiding is defence-in-depth only.
const _purchasingGatedCache = new WeakMap<
  React.ComponentType<unknown>,
  React.ComponentType<unknown>
>();
function purchasingGated(Component: React.ComponentType<unknown>) {
  const cached = _purchasingGatedCache.get(Component);
  if (cached) return cached;
  const Gated = function PurchasingGated(props: unknown) {
    const { has, isLoading, error } = useMe();
    const { hasFeature, isLoading: featuresLoading } = useMyFeatures();
    if ((isLoading && !error) || featuresLoading) return null;
    if (!hasFeature("purchasing_automation_enabled")) return <AccessDenied section="reports" />;
    if (!has("purchasing.view")) return <AccessDenied section="reports" />;
    return <Component {...(props as object)} />;
  };
  Gated.displayName = `PurchasingGated(${Component.displayName ?? Component.name ?? "Anon"})`;
  _purchasingGatedCache.set(Component, Gated);
  return Gated;
}

// Workflow Automation Builder gate (Task #919). Like forecasting, automations
// is NOT a UI "section" in the legacy sense — it gates on the platform-owner-
// only `automation_builder_enabled` feature flag AND the `automations.view`
// permission, the same two checks the backend route enforces (404/403 when
// off). FE hiding is defence-in-depth only.
const _automationsGatedCache = new WeakMap<
  React.ComponentType<unknown>,
  React.ComponentType<unknown>
>();
function automationsGated(Component: React.ComponentType<unknown>) {
  const cached = _automationsGatedCache.get(Component);
  if (cached) return cached;
  const Gated = function AutomationsGated(props: unknown) {
    const { has, isLoading, error } = useMe();
    const { hasFeature, isLoading: featuresLoading } = useMyFeatures();
    if ((isLoading && !error) || featuresLoading) return null;
    if (!hasFeature("automation_builder_enabled")) return <AccessDenied section="automations" />;
    if (!has("automations.view")) return <AccessDenied section="automations" />;
    return <Component {...(props as object)} />;
  };
  Gated.displayName = `AutomationsGated(${Component.displayName ?? Component.name ?? "Anon"})`;
  _automationsGatedCache.set(Component, Gated);
  return Gated;
}

function isUnauthorizedError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const status = (err as { status?: unknown }).status;
  return status === 401 || status === 403;
}

function AuthorisedRoutes() {
  const { signOut } = useClerk();
  const { me, isLoading, error } = useMe();
  const { tenantSuspended, isLoading: featuresLoading } = useMyFeatures();
  const signedOutRef = useRef(false);
  const unauthorized = isUnauthorizedError(error);
  // Once we have EVER successfully loaded /api/me, latch this to true and
  // never tear the route tree down again for transient query state. Without
  // this latch, a single re-render where react-query briefly reports
  // isLoading=true or surfaces a 401 (e.g. during session-token rotation,
  // an iframe focus blip, or a programmatic invalidate) would unmount the
  // entire <Switch>, wiping any in-progress form (e.g. a half-filled
  // multi-invoice purchase order — exactly the bug we are fixing).
  const everLoadedRef = useRef(false);
  if (me) everLoadedRef.current = true;
  const everLoaded = everLoadedRef.current;
  // A 401/403 is only "fatal" before we've ever loaded. After that we
  // assume it's transient and let react-query retry in the background
  // while keeping the cached `me` and the rendered routes intact.
  const fatalUnauthorized = unauthorized && !everLoaded;
  const [location] = useLocation();

  useEffect(() => {
    if (!fatalUnauthorized || signedOutRef.current) return;
    // Server rejected /api/me on first load. This may just be a race — the
    // Clerk auth bridge may not have registered its bearer-token getter
    // yet. Wait a few seconds; if /api/me is still unauthorized after
    // that, the session is genuinely bad and we sign the user out so
    // Clerk's <Show when="signed-out"> sibling can redirect to /sign-in
    // instead of leaving the page perpetually stuck on "Loading…".
    const t = setTimeout(() => {
      if (signedOutRef.current) return;
      signedOutRef.current = true;
      void signOut();
    }, 4000);
    return () => clearTimeout(t);
  }, [fatalUnauthorized, signOut]);

  if (fatalUnauthorized) {
    return <div className="p-8 text-muted-foreground">Re-checking your session…</div>;
  }
  if (isLoading && !everLoaded) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }
  if (me?.role === "pending") {
    // Pending users are otherwise gated out of the workspace, but they must
    // still be able to manage their own credentials (e.g. change password).
    // Allow ONLY the self-service /account route through, in a minimal frame
    // without the operator sidebar.
    if (location === "/account") {
      return (
        <div className="min-h-screen bg-background">
          <Suspense fallback={<RouteFallback />}>
            <AccountPage />
          </Suspense>
        </div>
      );
    }
    return <PendingApprovalScreen />;
  }
  // Task #1212: a suspended tenant (trial lapse or manual) is locked out of the
  // entire dashboard — including the mobile shell. Only the Client Area
  // (billing/plan/invoices/payments/support/account/help) stays reachable so
  // the client can settle up and restore access. The server enforces this
  // authoritatively via `tenantSuspendedGate`; this redirect is the UX layer so
  // they land on the Billing page instead of hitting 403s across the app.
  if (tenantSuspended && !featuresLoading) {
    const onClientArea =
      location === "/account" ||
      location === "/help" ||
      location.startsWith("/account-portal");
    if (!onClientArea) {
      return <Redirect to="/account-portal/billing" />;
    }
  }
  if (location === "/m" || location.startsWith("/m/")) {
    // Mobile-first operations shell (Task #925). Renders full-screen, OUTSIDE
    // the desktop MainLayout, and self-gates on the mobile_operations_enabled
    // feature key. Desktop is untouched when the feature is off.
    return (
      <Suspense fallback={<RouteFallback />}>
        <MobileApp />
      </Suspense>
    );
  }
  return (
    <MainLayout>
      <Suspense fallback={<RouteFallback />}>
      <RouteErrorBoundary routeLabel={location}>
      <Switch>
        <Route path="/ring-appstore/link" component={RingAppstoreLinkPage} />
        <Route path="/ring-appstore/home" component={RingAppstoreHomePage} />
        <Route path="/help" component={HelpPage} />
        <Route path="/operations/search" component={operationsGated("operations_universal_search", "operations.search.use", OperationsSearch)} />
        <Route path="/operations/checklists/manage" component={operationsGated("operations_checklists", "operations.checklists.manage", OperationsChecklistManage)} />
        <Route path="/operations/checklists" component={operationsGated("operations_checklists", "operations.checklists.view", OperationsChecklists)} />
        <Route path="/operations/feed" component={operationsGated("operations_feed", "operations.feed.view", OperationsFeed)} />
        <Route path="/operations/board" component={operationsGated("operations_status_board", "operations.status_board.view", OperationsBoard)} />
        <Route path="/operations/friction" component={operationsGated("operations_friction_analytics", "operations.friction_analytics.view", OperationsFriction)} />
        <Route path="/operations" component={operationsGated("operations_next_action", "operations.next_action.view", OperationsIndex)} />
        <Route path="/markets" component={featureGated(MARKET_TOOLS_FEATURE, "markets", MarketsDashboard)} />
        <Route path="/markets/live-prices" component={featureGated(MARKET_TOOLS_FEATURE, "markets", MarketsLivePrices)} />
        <Route path="/markets/calculator" component={featureGated(MARKET_TOOLS_FEATURE, "markets", MarketsCalculator)} />
        <Route path="/markets/charts" component={featureGated(MARKET_TOOLS_FEATURE, "markets", MarketsCharts)} />
        <Route path="/markets/ratios" component={featureGated(MARKET_TOOLS_FEATURE, "markets", MarketsRatios)} />
        <Route path="/markets/history" component={featureGated(MARKET_TOOLS_FEATURE, "markets", MarketsHistory)} />
        <Route path="/markets/converter" component={featureGated(MARKET_TOOLS_FEATURE, "markets", MarketsConverter)} />
        <Route path="/markets/dealer-calculator" component={featureGated(MARKET_TOOLS_FEATURE, "markets", MarketsDealerCalculator)} />
        <Route path="/markets/settings" component={featureGated(MARKET_TOOLS_FEATURE, "markets", MarketsSettings)} />
        <Route path="/markets/alerts" component={featureGated(MARKET_TOOLS_FEATURE, "markets", MarketAlertsContent)} />
        <Route path="/markets/help" component={featureGated(MARKET_TOOLS_FEATURE, "markets", MarketsHelp)} />
        <Route path="/account" component={AccountPage} />
        <Route path="/" component={gated("dashboard", Dashboard)} />
        <Route path="/inventory" component={gated("inventory", InventoryList)} />
        <Route path="/inventory/product-importer" component={gated("inventory", ProductImporterPage)} />
        <Route path="/inventory/live" component={gated("inventory", InventoryLiveView)} />
        <Route path="/inventory/repair-centre" component={gated("inventory", RepairCentre)} />
        <Route path="/inventory/catalogue-quality" component={catalogueIntelligenceGated(CatalogueQualityPage)} />
        <Route path="/inventory/valuation-migration" component={gated("inventory", ValuationMigrationPage)} />
        <Route path="/inventory/valuation-rules" component={gated("inventory", ValuationRulesPage)} />
        <Route path="/image-studio" component={featureGated("image_studio", "inventory", ImageStudioHome, "Image Studio")} />
        <Route path="/image-studio/projects/:id" component={featureGated("image_studio", "inventory", ImageStudioEditor, "Image Studio")} />
        {/* Phase 3 (Task #1871): Scene Builder — same image_studio feature gate;
            scene_builder.* permissions are enforced per action inside. */}
        <Route path="/image-studio/marketplace-presets" component={featureGated("image_studio", "inventory", MarketplacePresetsPage, "Image Studio")} />
        <Route path="/image-studio/composite-presets" component={featureGated("image_studio", "inventory", CompositePresetsPage, "Image Studio")} />
        <Route path="/image-studio/filter-effects" component={featureGated("image_studio", "inventory", FilterEffectsPage, "Image Studio")} />
        <Route path="/image-studio/scenes" component={featureGated("image_studio", "inventory", ImageStudioScenes, "Scene Builder")} />
        <Route path="/image-studio/scenes/:id" component={featureGated("image_studio", "inventory", SceneEditorPage, "Scene Builder")} />
        <Route path="/settings/valuation" component={gated("settings", ValuationSettingsPage)} />
        <Route path="/settings/inventory/stock-costing" component={gated("inventory", InventoryCostingSettingsPage)} />
        <Route path="/inventory/drafts/:id" component={gated("inventory", DraftFormPage)} />
        <Route path="/inventory/:id">
          {(params) => {
            const Gated = gated("inventory", InventoryDetail);
            return (
              <RouteErrorBoundary routeLabel={`/inventory/${params.id}`}>
                <Gated />
              </RouteErrorBoundary>
            );
          }}
        </Route>
        <Route path="/suppliers" component={gated("suppliers", SuppliersList)} />
        <Route path="/suppliers/:id" component={gated("suppliers", SupplierDetail)} />
        <Route path="/purchase-orders" component={gated("purchase_orders", PurchaseOrdersList)} />
        <Route
          path="/purchase-orders/new"
          component={permissionGated(
            "edit_purchase_orders",
            gated("purchase_orders", NewPurchaseOrder),
            "purchase_orders",
          )}
        />
        <Route path="/purchase-orders/shipment-groups" component={gated("purchase_orders", ShipmentGroupsList)} />
        <Route path="/purchase-orders/shipment-groups/:id" component={gated("purchase_orders", ShipmentGroupDetail)} />
        <Route path="/purchase-orders/:id" component={gated("purchase_orders", PurchaseOrderDetail)} />
        <Route path="/pre-orders" component={StaffPreOrderBatchList} />
        <Route path="/pre-orders/:id" component={StaffPreOrderBatchDetail} />
        {/* Customer Commerce Portal (Task #2242). The server remains
            authoritative for each feature, sub-entitlement and permission. */}
        <Route path="/commerce" component={featureGated("customer_commerce_portal", "commerce", CommerceHome)} />
        <Route path="/commerce/dashboard" component={featureGated("customer_commerce_portal", "commerce", CommerceDashboard)} />
        <Route path="/commerce/products" component={featureGated("customer_commerce_portal", "commerce", CommerceProducts)} />
        <Route path="/commerce/categories" component={featureGated("customer_commerce_portal", "commerce", CommerceCategories)} />
        <Route path="/commerce/collections" component={featureGated("customer_commerce_portal", "commerce", CommerceCollections)} />
        <Route path="/commerce/boxes" component={featureGated("customer_commerce_portal", "commerce", CommerceBoxes)} />
        <Route path="/commerce/checkouts" component={featureGated("customer_commerce_portal", "commerce", CommerceCheckouts)} />
        <Route path="/commerce/awaiting-payment" component={featureGated("customer_commerce_portal", "commerce", CommerceAwaitingPayment)} />
        <Route path="/commerce/pre-orders" component={CommercePreOrderBatchList} />
        <Route path="/commerce/pre-orders/:id" component={CommercePreOrderBatchDetail} />
        <Route path="/commerce/pre-sales" component={CommercePreSalesBatchList} />
        <Route path="/commerce/pre-sales/:id" component={CommercePreSalesBatchDetail} />
        <Route path="/commerce/customers" component={featureGated("customer_commerce_portal", "commerce", CommerceCustomers)} />
        <Route path="/commerce/customer-groups" component={featureGated("customer_commerce_portal", "commerce", CommerceCustomerGroups)} />
        <Route path="/commerce/promotions" component={featureGated("customer_commerce_portal", "commerce", CommercePricing)} />
        <Route path="/commerce/pricing" component={featureGated("customer_commerce_portal", "commerce", CommercePricing)} />
        <Route path="/commerce/quotes" component={featureGated("customer_commerce_portal", "commerce", CommerceQuotes)} />
        <Route path="/commerce/payments" component={featureGated("customer_commerce_portal", "commerce", CommercePayments)} />
        <Route path="/commerce/postage" component={CommercePostageGated} />
        <Route path="/commerce/portal-editor" component={featureGated("customer_commerce_portal", "commerce", CommercePortalEditor)} />
        <Route path="/commerce/analytics" component={featureGated("customer_commerce_portal", "commerce", CommerceAnalytics)} />
        <Route path="/commerce/settings" component={featureGated("customer_commerce_portal", "commerce", CommerceSettings)} />
        <Route path="/commerce/help" component={featureGated("customer_commerce_portal", "commerce", CommerceHelp)} />
        <Route
          path="/customer-portfolios"
          component={featureGated(
            "customer_portfolios_enabled",
            "customer_portfolios",
            CustomerPortfoliosIndex,
            "Customer Portfolios",
          )}
        />
        <Route
          path="/customer-portfolios/customers"
          component={featureGated(
            "customer_portfolios_enabled",
            "customer_portfolios",
            CustomerPortfoliosIndex,
            "Customer Portfolios",
          )}
        />
        <Route
          path="/customer-portfolios/customers/:id"
          component={featureGated(
            "customer_portfolios_enabled",
            "customer_portfolios",
            CustomerPortfolioCustomer,
            "Customer Portfolios",
          )}
        />
        <Route
          path="/customer-portfolios/analytics"
          component={featureGated(
            "customer_portfolios_enabled",
            "customer_portfolios",
            CustomerPortfolioAnalytics,
            "Customer Portfolios",
          )}
        />
        <Route
          path="/customer-portfolios/settings"
          component={featureGated(
            "customer_portfolios_enabled",
            "customer_portfolios",
            CustomerPortfolioSettings,
            "Customer Portfolios",
          )}
        />
        <Route
          path="/customer-portfolios/branding"
          component={featureGated(
            "customer_portfolios_enabled",
            "customer_portfolios",
            CustomerPortfolioBranding,
            "Customer Portfolios",
          )}
        />
        <Route
          path="/customer-portfolios/public-markets"
          component={featureGated(
            "customer_portfolios_enabled",
            "customer_portfolios",
            PublicMarketsBuilder,
            "Public Markets",
          )}
        />
        <Route
          path="/customer-portfolios/products"
          component={featureGated(
            "customer_portfolios_enabled",
            "customer_portfolios",
            CustomerPortfolioProducts,
            "Customer Portfolios",
          )}
        />
        <Route
          path="/customer-portfolios/:id"
          component={featureGated(
            "customer_portfolios_enabled",
            "customer_portfolios",
            CustomerPortfolioDetail,
            "Customer Portfolios",
          )}
        />
        <Route path="/exceptions" component={featureGated("exception_management_enabled", "exceptions", OperationsInbox, "Operations Inbox")} />
        <Route path="/exceptions/rules" component={featureGated("exception_management_enabled", "exceptions", ExceptionRulesPage, "Operations Inbox")} />
        <Route path="/customers" component={gated("customers", CustomersList)} />
        <Route path="/customer-support" component={CustomerSupportRoute} />
        <Route path="/customers/new" component={gated("customers", NewCustomer)} />
        <Route path="/customers/merge" component={featureGated("customer_merge_enabled", "customers", MergeCustomers)} />
        <Route path="/customers/:id/edit" component={gated("customers", EditCustomer)} />
        <Route path="/customers/:id/:tab" component={gated("customers", CustomerDetail)} />
        <Route path="/customers/:id" component={gated("customers", CustomerDetail)} />
        <Route path="/companies" component={gated("customers", CompaniesList)} />
        <Route path="/companies/new" component={gated("customers", NewCompany)} />
        <Route path="/companies/:id/edit" component={gated("customers", EditCompany)} />
        <Route path="/companies/:id" component={gated("customers", CompanyDetail)} />
        <Route path="/sales" component={gated("sales", SalesList)} />
        <Route path="/sales/new" component={gated("sales", NewSale)} />
        <Route path="/sales/import" component={gated("sales", SalesImport)} />
        <Route path="/sales/whatnot-fees" component={gated("sales", permissionGated("edit_sales", WhatnotFeesAdjuster, "sales"))} />
        <Route path="/sales/whatsapp-automations" component={WhatsappAutomationsPage} />
        <Route path="/whatsapp-tracking" component={WhatsappTrackingPage} />
        <Route path="/imports/:logId/issues" component={gated("sales", ImportIssuesPage)} />
        <Route path="/sales/:id" component={gated("sales", SaleDetail)} />

        <Route path="/vat-due" component={gated("admin", VatDuePage)} />

        <Route path="/adjustments" component={gated("sales", AdjustmentsList)} />
        <Route path="/invoices" component={gated("sales", InvoicesList)} />
        <Route path="/invoices/new" component={gated("sales", NewInvoice)} />
        <Route path="/invoices/drafts/:id" component={gated("sales", EditInvoiceDraft)} />
        <Route path="/invoices/:id" component={gated("sales", InvoiceDetail)} />
        <Route path="/channels" component={gated("channels", Channels)} />
        <Route path="/integrations/logs" component={gated("integrations", IntegrationLogs)} />
        <Route path="/integrations/:slug/logs" component={gated("integrations", IntegrationLogs)} />
        <Route path="/integrations/:slug" component={gated("integrations", IntegrationDetail)} />
        <Route path="/integrations" component={gated("integrations", IntegrationsIndex)} />
        <Route path="/quickbooks" component={gated("integrations", QuickBooksSyncLog)} />
        <Route path="/xero" component={gated("integrations", XeroSyncLog)} />
        <Route path="/categories" component={gated("inventory", CategoriesPage)} />
        <Route path="/title-mappings" component={gated("inventory", TitleMappingsPage)} />
        <Route path="/admin/tiktok-mappings" component={gated("admin", TikTokMappingsPage)} />
        <Route path="/tiktok-shop" component={featureGated("tiktok_shop_integration_enabled", "tiktok_shop", TikTokShopDashboardPage, "TikTok Shop")} />
        <Route path="/tiktok-shop/products" component={featureGated("tiktok_shop_integration_enabled", "tiktok_shop", TikTokShopProductsPage, "TikTok Shop")} />
        <Route path="/tiktok-shop/orders" component={featureGated("tiktok_shop_integration_enabled", "tiktok_shop", TikTokShopOrdersPage, "TikTok Shop")} />
        <Route path="/tiktok-shop/messages" component={featureGated("tiktok_shop_integration_enabled", "tiktok_shop", TikTokShopMessagesPage, "TikTok Shop")} />
        <Route path="/tiktok-shop/sync" component={featureGated("tiktok_shop_integration_enabled", "tiktok_shop", TikTokShopSyncCentrePage, "TikTok Shop")} />
        <Route path="/tiktok-shop/developer" component={featureGated("tiktok_shop_integration_enabled", "tiktok_shop", TikTokShopDeveloperPage, "TikTok Shop")} />
        <Route path="/tiktok-shop/connections" component={featureGated("tiktok_shop_integration_enabled", "tiktok_shop", TikTokShopConnectionsPage, "TikTok Shop")} />
        <Route path="/tiktok-shop/settings" component={featureGated("tiktok_shop_integration_enabled", "tiktok_shop", TikTokShopSettingsPage, "TikTok Shop")} />
        <Route path="/tiktok-shop/audit" component={featureGated("tiktok_shop_integration_enabled", "tiktok_shop", TikTokShopAuditPage, "TikTok Shop")} />
        <Route path="/tiktok-shop/push-listings" component={featureGated("tiktok_shop_integration_enabled", "tiktok_shop", TikTokShopPushListingsPage, "TikTok Shop")} />
        <Route path="/admin/system-health" component={platformWorkspaceGated("health", AdminHealthWorkspace)} />
        <Route path="/admin/system-health/legacy" component={platformOperatorGated(LegacySystemHealthPage)} />
        <Route path="/admin/worker-dashboard" component={gated("admin", WorkerDashboardPage)} />
        <Route path="/admin/background-jobs" component={gated("admin", BackgroundJobsAdminPage)} />
        <Route path="/admin/performance-centre" component={gated("admin", PerformanceCentrePage)} />
        <Route path="/messages" component={gated("messages", MessagesPage)} />
        <Route path="/whatsapp-inbox/settings" component={WhatsappInboxSettingsPage} />
        <Route path="/whatsapp-inbox/analytics" component={WhatsappInboxAnalyticsPage} />
        <Route path="/whatsapp-inbox" component={WhatsappInboxPage} />
        <Route path="/packing/pick-list/:importId/slips" component={featureGated("packing_module_enabled", "packing", PickListSlips, "Packing Module")} />
        <Route path="/packing/pick-list/:importId" component={featureGated("packing_module_enabled", "packing", PickListDetail, "Packing Module")} />
        <Route path="/packing/pick-list" component={featureGated("packing_module_enabled", "packing", PickListIndex, "Packing Module")} />
        <Route path="/packing/pdf-splitter" component={featureGated("packing_module_enabled", "packing", PdfSplitter, "Packing Module")} />
        <Route path="/packing/jobs/:id" component={featureGated("packing_module_enabled", "packing", PackingJobDetail, "Packing Module")} />
        <Route path="/packing" component={featureGated("packing_module_enabled", "packing", Packing, "Packing Module")} />
        <Route path="/print-pack" component={featureGated("packing_module_enabled", "print_pack", PrintPackStation, "Packing Module")} />
        <Route path="/expenses" component={gated("expenses", Expenses)} />
        <Route path="/stationery" component={gated("stationery", Stationery)} />
        <Route path="/reports/advanced" component={featureGated("advanced_analytics_enabled", "reports", AdvancedAnalytics)} />
        <Route path="/reports/customer-insights" component={gated("reports", CustomerInsights)} />
        <Route path="/reports/hazel">
          <ReportsRouteGate><HazelReportsPage /></ReportsRouteGate>
        </Route>
        <Route path="/reports/credit-control">
          <ReportsRouteGate><ScheduledCreditReporting /></ReportsRouteGate>
        </Route>
        <Route path="/reports" component={ReportsRoute} />
        <Route path="/reports/merges" component={gated("reports", MergeReport)} />
        <Route path="/reports/forecasting" component={forecastingGated(ForecastingPage)} />
        <Route path="/reports/intelligence" component={intelligenceGated(IntelligencePage)} />
        <Route path="/reports/purchasing" component={purchasingGated(PurchasingPage)} />
        <Route path="/automations" component={automationsGated(AutomationsPage)} />
        <Route path="/automations/logs" component={automationsGated(AutomationLogsPage)} />
        <Route path="/automations/new" component={automationsGated(AutomationBuilderPage)} />
        <Route path="/automations/:id" component={automationsGated(AutomationBuilderPage)} />
        <Route path="/profit-loss" component={gated("reports", ProfitLoss)} />
        <Route path="/sales-performance" component={gated("reports", SalesPerformance)} />
        {/* Product Performance is an owner-only add-on: the API mounts the whole
            `/product-performance` router behind `product_performance_enabled`, so the
            route must gate on the SAME feature or a deep link renders a page that
            immediately 403s (Task #2375). No `featureLabel` — owner-only features
            cannot be self-serve requested. */}
        <Route path="/product-performance/:id" component={featureGated("product_performance_enabled", "dashboard", ProductPerformance)} />
        <Route path="/product-performance" component={featureGated("product_performance_enabled", "dashboard", ProductPerformance)} />
        <Route path="/demand/products/:id" component={DemandIntelligenceRoute} />
        <Route path="/demand/behaviour" component={DemandIntelligenceRoute} />
        <Route path="/demand/customers" component={DemandIntelligenceRoute} />
        <Route path="/demand/products" component={DemandIntelligenceRoute} />
        <Route path="/demand/channels" component={DemandIntelligenceRoute} />
        <Route path="/demand/forecast" component={DemandIntelligenceRoute} />
        <Route path="/demand/purchase-planner" component={DemandIntelligenceRoute} />
        <Route path="/demand/targets" component={DemandIntelligenceRoute} />
        <Route path="/demand/insights" component={DemandIntelligenceRoute} />
        <Route path="/demand" component={DemandIntelligenceRoute} />
        <Route path="/settings" component={gated("settings", Settings)} />
        <Route path="/settings/brand-options">
          <Redirect to={LEGACY_BRAND_CUSTOMISATION_REDIRECTS["/settings/brand-options"]} />
        </Route>
        <Route path="/settings/invoice-template">
          <Redirect to={LEGACY_BRAND_CUSTOMISATION_REDIRECTS["/settings/invoice-template"]} />
        </Route>
        <Route path="/settings/notifications-preferences" component={gated("settings", NotificationPreferences)} />
        <Route path="/settings/notifications-defaults" component={gated("settings", NotificationDefaults)} />
        <Route path="/settings/notifications-thresholds" component={gated("settings", NotificationThresholds)} />
        <Route path="/settings/ai-product-copy" component={gated("settings", AiProductCopySettings)} />
        <Route path="/settings/background-activity" component={gated("settings", BackgroundActivityPage)} />
        <Route path="/email-center" component={gated("settings", EmailCenter)} />
        <Route path="/notifications" component={gated("settings", NotificationsPage)} />
        <Route path="/email-campaigns" component={gated("settings", EmailCampaigns)} />
        <Route path="/email-campaigns/:id" component={gated("settings", EmailCampaignAnalytics)} />
        <Route path="/account-portal" component={gated("account", AccountPortal)} />
        <Route path="/account-portal/features" component={gated("account", AccountFeatures)} />
        <Route path="/account-portal/requests" component={gated("account", AccountRequests)} />
        <Route path="/account-portal/requests/new" component={gated("account", AccountRequestNew)} />
        <Route path="/account-portal/requests/:id" component={gated("account", AccountRequestDetail)} />
        <Route path="/account-portal/plan" component={gated("account", AccountPlan)} />
        <Route path="/account-portal/usage" component={gated("account", AccountUsage)} />
        <Route path="/account-portal/portfolio-domains" component={gated("account", PortfolioDomains)} />
        <Route path="/account-portal/billing" component={gated("account", AccountBilling)} />
        <Route path="/account-portal/invoices" component={gated("account", AccountInvoices)} />
        <Route path="/account-portal/payments" component={gated("account", AccountPayments)} />
        <Route path="/account-portal/billing/paypal/return" component={gated("account", AccountPaypalReturn)} />
        <Route path="/account-portal/users" component={gated("account", AccountUsers)} />
        <Route path="/account-portal/business" component={gated("account", AccountBusiness)} />
        <Route path="/account-portal/trial" component={gated("account", AccountTrial)} />
        <Route path="/account-portal/support" component={gated("account", AccountSupport)} />
        <Route path="/account-portal/notifications" component={gated("account", AccountNotifications)} />
        <Route path="/account-portal/security" component={gated("account", AccountSecurity)} />
        <Route path="/account-portal/help" component={gated("account", AccountHelp)} />
        <Route path="/admin" component={() => <Redirect to="/admin/tenants" />} />
        <Route path="/admin/invoice-branding">
          <Redirect to={LEGACY_BRAND_CUSTOMISATION_REDIRECTS["/admin/invoice-branding"]} />
        </Route>
        <Route path="/admin/brand-customisation/dashboard" component={permissionGated("edit_settings", DashboardBrandCustomisation)} />
        <Route path="/admin/brand-customisation/invoices" component={permissionGated("edit_settings", InvoiceBrandCustomisation)} />
        <Route path="/admin/brand-customisation" component={permissionGated("edit_settings", BrandCustomisation)} />
        <Route path="/admin/hero-shot-settings" component={gated("admin", AdminHeroShotSettings)} />
        <Route path="/admin/user-permissions" component={gated("admin", UserPermissionsPage)} />
        <Route path="/admin/live-stock-export-users" component={gated("admin", LiveStockExportUsersPage)} />
        {/* Reconciliation is gated by the inventory section so it matches the
            view_inventory permission on the underlying API — admins also pass
            the section check, so listing it under /admin/* is purely for URL
            organization. */}
        <Route path="/admin/reconciliation" component={gated("inventory", ReconciliationPage)} />
        <Route path="/admin/sidebar-manager/new" component={gated("admin", SidebarItemFormPage)} />
        <Route path="/admin/sidebar-manager/items/:id" component={gated("admin", SidebarItemFormPage)} />
        <Route path="/admin/sidebar-manager" component={gated("admin", SidebarManagerPage)} />
        <Route path="/admin/flags" component={gated("admin", FlagsAdminPage)} />
        <Route path="/admin/ai-tools" component={gated("admin", AiToolsAdminPage)} />
        <Route path="/admin/workflows" component={gated("admin", WorkflowsAdminPage)} />
        <Route path="/admin/custom-fields" component={gated("admin", CustomFieldsAdminPage)} />
        <Route path="/admin/activity-log" component={gated("activity_log", ActivityLog)} />
        <Route path="/admin/audit-log" component={gated("admin", AuditLogBrowser)} />
        <Route path="/admin/sales-reallocation" component={gated("admin", SalesReallocationPage)} />
        <Route path="/admin/consignment-rebalance" component={gated("admin", ConsignmentRebalancePage)} />
        <Route path="/admin/shopify-stub-reconcile" component={gated("admin", ShopifyStubReconcilePage)} />
        <Route path="/admin/transformations" component={gated("admin", TransformationsAdminPage)} />
        <Route path="/admin/image-templates" component={gated("admin", ImageTemplatesPage)} />
        <Route path="/admin/tenants" component={platformWorkspaceGated("tenants", AdminTenantsWorkspace)} />
        <Route path="/admin/tenants/manage" component={gated("admin", LegacyTenantsAdminPage)} />
        <Route path="/admin/tenants/:tenantId" component={platformWorkspaceGated("tenants", AdminTenantDetailWorkspace)} />
        <Route path="/admin/users" component={platformWorkspaceGated("users", AdminUsersWorkspace)} />
        <Route path="/admin/users/:userId" component={platformWorkspaceGated("users", AdminUserDetailWorkspace)} />
        <Route path="/admin/roles" component={platformWorkspaceGated("roles", AdminRolesWorkspace)} />
        <Route path="/admin/roles-permissions" component={platformWorkspaceGated("roles", AdminRolesWorkspace)} />
        <Route path="/admin/settings" component={platformWorkspaceGated("settings", AdminSettingsWorkspace)} />
        <Route path="/admin/platform-settings" component={platformWorkspaceGated("settings", AdminSettingsWorkspace)} />
        <Route path="/admin/billing" component={platformWorkspaceGated("billing", AdminBillingWorkspace)} />
        {/* MetalUI platform-operator surface. Intentionally NOT wrapped
            in `gated(...)` — the section-access map is per-tenant and a
            cross-tenant operator action shouldn't be vetoed by the
            caller's local section flags. The page itself hard-gates on
            `useIsPlatformOperator()` and every backing API returns
            uniform 404 to non-platform callers. */}
        {/* Tenants Dashboard (Task #1354) — platform-operator cross-tenant
            health overview. Same "not wrapped in gated(...)" rationale as the
            other /platform/* surfaces above: page hard-gates on
            `useIsPlatformOperator()`, and the backing aggregation route is
            further gated by the dedicated `view_tenant_dashboard` platform
            permission (uniform 404 to non-permitted callers). Nav sub-items
            besides Dashboard link to the existing /platform/* & /admin/*
            pages above, unchanged. */}
        <Route path="/tenants/dashboard" component={TenantsDashboard} />
        {/* Tenants Dashboard Phase 2 (Task #1355) — drill-down + sub-pages.
            Same "not wrapped in gated(...)" rationale: each page hard-gates on
            `useIsPlatformOperator()`, and every backing route is further
            gated by its own dedicated platform permission (uniform 404 to
            non-permitted callers). Registered BEFORE the parent
            `/tenants/dashboard` route above would matter for prefix
            collisions, but wouter matches literal `:param` routes fine after
            the exact-path route since `/tenants/dashboard` has no params. */}
        <Route path="/tenants/dashboard/:tenantId" component={TenantDetail} />
        {/* Legacy alert-link redirects (Task #1895) — platform alerts were
            historically persisted with `/platform/tenants-dashboard...` links
            that never matched any FE route. Redirect them to the real pages
            so already-stored alerts still resolve. The literal service-peaks
            route is registered before the :tenantId param route. */}
        <Route path="/platform/tenants-dashboard/service-peaks">
          <Redirect to="/tenants/service-peaks" />
        </Route>
        <Route path="/platform/tenants-dashboard/:tenantId">
          {(params) => <Redirect to={`/tenants/dashboard/${params.tenantId}`} />}
        </Route>
        <Route path="/platform/tenants-dashboard">
          <Redirect to="/tenants/dashboard" />
        </Route>
        <Route path="/tenants/service-peaks" component={ServicePeaks} />
        <Route path="/tenants/feature-usage" component={FeatureUsage} />
        <Route path="/platform/feature-management" component={PlatformFeatureManagement} />
        <Route path="/platform/website-builder" component={PlatformWebsiteBuilder} />
        <Route path="/public-site" component={PublicSiteContentStudio} />
        <Route path="/platform/plans-pricing" component={PlatformPlansPricing} />
        <Route path="/platform/memberships" component={PlatformMemberships} />
        <Route path="/platform/billing" component={PlatformBilling} />
        <Route path="/platform/enquiries" component={PlatformEnquiries} />
        <Route path="/platform/support" component={PlatformSupport} />
        <Route path="/platform/secrets" component={PlatformSecrets} />
        <Route path="/platform/ai-prompt-templates" component={PlatformAiPromptTemplates} />
        <Route path="/platform/market-data" component={PlatformMarketData} />
        <Route path="/platform/help-analytics" component={PlatformHelpAnalytics} />
        <Route path="/admin/losses" component={gated("admin", IncidentCenter)} />
        <Route path="/admin/ebay-sync-issues" component={gated("admin", EbaySyncIssuesPage)} />
        <Route path="/channels/whatnot-stock-issues" component={gated("channels", WhatnotStockIssuesPage)} />
        <Route path="/admin/ebay-sync-runs" component={gated("admin", EbaySyncRunsPage)} />
        {/* eBay Feedback Management — gated by the `channels` section, since
            it lives under the marketplaces tree. The page itself also gates
            its mutations server-side via the `manage_ebay_feedback`
            permission, so non-admin users in the channels section who lack
            that permission will see read-only behaviour. */}
        <Route path="/marketplaces/ebay-feedback" component={featureGated("ebay_feedback_enabled", "channels", EbayFeedbackPage, "eBay Feedback")} />
        <Route path="/social/compose" component={featureGated("social_module_enabled", "social", SocialCompose)} />
        <Route path="/social/calendar" component={featureGated("social_module_enabled", "social", SocialCalendar)} />
        <Route path="/social/scheduled" component={featureGated("social_module_enabled", "social", SocialScheduled)} />
        <Route path="/social/drafts" component={featureGated("social_module_enabled", "social", SocialDrafts)} />
        <Route path="/social/published" component={featureGated("social_module_enabled", "social", SocialPublished)} />
        <Route path="/social/approvals" component={featureGated("social_module_enabled", "social", SocialApprovals)} />
        <Route path="/social/media" component={featureGated("social_module_enabled", "social", SocialMediaLibrary)} />
        <Route path="/social/gallery" component={featureGated("social_module_enabled", "social", SocialGallery)} />
        <Route path="/social/brand-kit" component={featureGated("social_module_enabled", "social", SocialBrandKit)} />
        <Route path="/social/templates" component={featureGated("social_module_enabled", "social", SocialTemplates)} />
        <Route path="/social/studio" component={featureGated("social_module_enabled", "social", SocialStudio)} />
        <Route path="/social/campaigns" component={featureGated("social_module_enabled", "social", SocialCampaigns)} />
        <Route path="/social/analytics" component={featureGated("social_module_enabled", "social", SocialAnalytics)} />
        <Route path="/social/inbox" component={featureGated("social_module_enabled", "social", SocialInbox)} />
        <Route path="/social/integrations" component={featureGated("social_module_enabled", "social", SocialIntegrations)} />
        <Route path="/social/settings" component={featureGated("social_module_enabled", "social", SocialSettings)} />
        <Route path="/social/audit" component={featureGated("social_module_enabled", "social", SocialAudit)} />
        <Route path="/social/history" component={featureGated("social_module_enabled", "social", SocialHistory)} />
        <Route path="/social" component={featureGated("social_module_enabled", "social", SocialHub)} />
        <Route path="/dropshipping" component={gated("dropshipping", DropshippingDashboard)} />
        <Route path="/dropshipping/customers" component={gated("dropshipping", DropshippingCustomers)} />
        <Route path="/dropshipping/customers/:id" component={gated("dropshipping", DropshippingCustomerDetail)} />
        <Route path="/consignment-sync/incoming" component={gated("consignment_sync", ConsignmentSyncIncoming)} />
        <Route path="/consignment-sync/outgoing" component={gated("consignment_sync", ConsignmentSyncOutgoing)} />
        <Route path="/consignment-sync" component={gated("consignment_sync", ConsignmentSyncPartnerships)} />
        <Route path="/consignment" component={gated("consignment", ConsignmentWorkspace)} />
        <Route path="/consignment/legacy-dashboard" component={gated("consignment", ConsignmentDashboard)} />
        <Route path="/consignment/inventory" component={gated("consignment", ConsignmentWorkspace)} />
        <Route path="/consignment/consignors" component={gated("consignment", ConsignmentWorkspace)} />
        <Route path="/consignment/sales" component={gated("consignment", ConsignmentWorkspace)} />
        <Route path="/consignment/settlements" component={gated("consignment", ConsignmentWorkspace)} />
        <Route path="/consignment/agreements" component={gated("consignment", ConsignmentWorkspace)} />
        <Route path="/consignment/exceptions" component={gated("consignment", ConsignmentWorkspace)} />
        <Route path="/consignment/stock" component={gated("consignment", ConsignmentStock)} />
        <Route path="/consignment/suppliers" component={gated("consignment", ConsignmentSuppliers)} />
        <Route path="/consignment/suppliers/:id" component={gated("consignment", ConsignmentSupplierDetail)} />
        <Route
          path="/consignment/suppliers/:id/sku-payouts/:groupKey"
          component={gated("consignment", ConsignmentSkuPayoutDetail)}
        />
        <Route path="/consignment/payouts" component={gated("consignment", ConsignmentPayouts)} />
        <Route path="/consignment/payouts/:id" component={gated("consignment", ConsignmentPayoutDetail)} />
        <Route path="/consignment/statements/:id" component={gated("consignment", ConsignmentStatementDetail)} />
        <Route path="/consignment/batches" component={gated("consignment", ConsignmentBatches)} />
        <Route path="/consignment/batches/:id" component={gated("consignment", ConsignmentBatchDetail)} />
        <Route path="/tasks" component={featureGated("tasks_module_enabled", "tasks", MyTasks, "Tasks Module")} />
        <Route path="/tasks/team" component={featureGated("tasks_module_enabled", "tasks", TeamTasks, "Tasks Module")} />
        <Route path="/tasks/completed" component={featureGated("tasks_module_enabled", "tasks", CompletedTasks, "Tasks Module")} />
        <Route path="/tasks/reminders" component={featureGated("tasks_module_enabled", "tasks", TaskReminders, "Tasks Module")} />
        <Route path="/tasks/calendar" component={featureGated("tasks_module_enabled", "tasks", TaskCalendar, "Tasks Module")} />
        <Route path="/tasks/settings" component={featureGated("tasks_module_enabled", "tasks", TaskSettings, "Tasks Module")} />
        <Route path="/tasks/projects" component={featureGated("tasks_module_enabled", "tasks", TaskProjects, "Tasks Module")} />
        <Route path="/tasks/projects/:id" component={featureGated("tasks_module_enabled", "tasks", TaskProjectDetail, "Tasks Module")} />
        <Route path="/tasks/templates" component={featureGated("tasks_module_enabled", "tasks", TaskTemplates, "Tasks Module")} />
        <Route path="/tasks/:id" component={featureGated("tasks_module_enabled", "tasks", TaskDetail, "Tasks Module")} />
        {/* Scrap module (Task #99) — order matters: specific before param/prefix. */}
        <Route path="/scrap/dashboard" component={scrapGated(ScrapDashboard)} />
        <Route path="/scrap/sell-to-us" component={SellToUsRoute} />
        <Route path="/scrap/purchases/new" component={scrapGated(ScrapPurchaseNew)} />
        <Route path="/scrap/purchases/:id" component={scrapGated(ScrapPurchaseDetail)} />
        <Route path="/scrap/purchases" component={scrapGated(ScrapPurchasesList)} />
        <Route path="/scrap/inventory" component={scrapGated(ScrapInventory)} />
        <Route path="/scrap/refining/results" component={scrapGated(ScrapRefiningResults)} />
        <Route path="/scrap/refining/:id" component={scrapGated(ScrapRefiningDetail)} />
        <Route path="/scrap/refining" component={scrapGated(ScrapRefiningList)} />
        <Route path="/scrap/reports" component={scrapGated(ScrapReports)} />
        <Route path="/scrap/settings" component={scrapGated(ScrapSettings)} />
        <Route path="/scrap"><Redirect to="/scrap/dashboard" /></Route>
        {/* Returns module — order matters: specific before param/prefix. */}
        <Route path="/returns/settings" component={featureGated("returns_module_enabled", "returns", ReturnsSettings)} />
        <Route path="/returns/reports" component={featureGated("returns_module_enabled", "returns", ReturnsReports)} />
        <Route path="/returns/list" component={featureGated("returns_module_enabled", "returns", ReturnsList)} />
        <Route path="/returns/:id" component={featureGated("returns_module_enabled", "returns", ReturnsDetail)} />
        <Route path="/returns" component={featureGated("returns_module_enabled", "returns", ReturnsOverview)} />
        <Route path="/evidence" component={featureGated("evidence_centre_enabled", "evidence", EvidenceCentre)} />
        {/* Document Generation module — order matters: specific before param/prefix. */}
        <Route path="/documents/templates" component={featureGated("document_generation_enabled", "documents", DocumentTemplates)} />
        <Route path="/documents/generated" component={featureGated("document_generation_enabled", "documents", GeneratedDocuments)} />
        <Route path="/documents/activity" component={featureGated("document_generation_enabled", "documents", DocumentsLog)} />
        <Route path="/documents" component={featureGated("document_generation_enabled", "documents", DocumentsOverview)} />
        {/* Asset Tracking module — order matters: specific before param/prefix. */}
        <Route path="/assets/list" component={featureGated("asset_tracking_enabled", "asset_tracking", AssetsList)} />
        <Route path="/assets/:id" component={featureGated("asset_tracking_enabled", "asset_tracking", AssetsDetail)} />
        <Route path="/assets" component={featureGated("asset_tracking_enabled", "asset_tracking", AssetsOverview)} />

        {/* Warehouse (WMS) module — order matters: specific before param/prefix. */}
        <Route path="/warehouse/locations" component={featureGated("wms_enabled", "warehouse", WarehouseLocations)} />
        <Route path="/warehouse" component={featureGated("wms_enabled", "warehouse", WarehouseOverview)} />
        <Route path="/compliance/tasks" component={featureGated("compliance_vault_enabled", "compliance", ComplianceTasks)} />
        <Route path="/compliance/profiles/:id" component={featureGated("compliance_vault_enabled", "compliance", ComplianceProfileDetail)} />
        <Route path="/compliance/profiles" component={featureGated("compliance_vault_enabled", "compliance", ComplianceProfiles)} />
        <Route path="/compliance" component={featureGated("compliance_vault_enabled", "compliance", ComplianceOverview)} />

        {/* Postage & Shipping module — order matters: specific before param/prefix. */}
        <Route path="/postage/shipments" component={featureGated("postage_module_enabled", "postage", PostageShipments)} />
        <Route path="/postage/combined" component={featureGated("postage_module_enabled", "postage", PostageCombined)} />
        <Route path="/postage/batch" component={featureGated("postage_module_enabled", "postage", PostageBatch)} />
        <Route path="/postage/couriers" component={featureGated("postage_module_enabled", "postage", PostageCouriers)} />
        <Route path="/postage/reports" component={featureGated("postage_module_enabled", "postage", PostageReports)} />
        <Route path="/postage/settings" component={featureGated("postage_module_enabled", "postage", PostageSettings)} />
        <Route path="/postage/audit" component={featureGated("postage_module_enabled", "postage", PostageAudit)} />
        <Route path="/postage" component={featureGated("postage_module_enabled", "postage", PostageOverview)} />
        {/* Platforms > Perks — Streaming & Live Selling Tools (Task #1035). */}
        {/* Order matters: specific paths before the parent. */}
        <Route path="/markets/overlay-studio/editor" component={featureGated("platform_perks_enabled", "perks", PerksOverlayDesigner, "Market Overlay Studio")} />
        <Route path="/markets/overlay-studio" component={featureGated("platform_perks_enabled", "perks", MarketOverlayStudio, "Market Overlay Studio")} />
        <Route path="/platforms/perks/streaming-tools" component={featureGated("platform_perks_enabled", "perks", PerksStreamingTools)} />
        <Route path="/platforms/perks/overlay-designer" component={featureGated("platform_perks_enabled", "perks", PerksOverlayDesigner)} />
        <Route path="/platforms/perks/product-queue" component={featureGated("platform_perks_enabled", "perks", PerksProductQueue)} />
        <Route path="/platforms/perks/live-event" component={featureGated("platform_perks_enabled", "perks", PerksLiveEvent)} />
        <Route path="/platforms/perks" component={featureGated("platform_perks_enabled", "perks", PerksIndex)} />
        {/* Dynamic Pricing module (Task #923, expanded in #2171) — order
            matters: literal child routes BEFORE the parent catch-all. The old
            /pricing/approvals and /pricing/run-logs tabs are now
            /pricing/recommendations and /pricing/history; the legacy paths are
            kept as aliases so bookmarks and existing links keep working. */}
        <Route path="/pricing/rules" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingRulesPage)} />
        <Route path="/pricing/recommendations" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingRecommendationsPage)} />
        <Route path="/pricing/approvals" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingRecommendationsPage)} />
        <Route path="/pricing/margin-protection" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingMarginProtectionPage)} />
        <Route path="/pricing/marketplace" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingMarketplacePage)} />
        <Route path="/pricing/premium-products" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingPremiumProductsPage)} />
        <Route path="/pricing/spot-products" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingSpotProductsPage)} />
        <Route path="/pricing/scheduled" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingScheduledPage)} />
        <Route path="/pricing/history" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingHistoryPage)} />
        <Route path="/pricing/run-logs" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingHistoryPage)} />
        <Route path="/pricing/exceptions" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingExceptionsPage)} />
        <Route path="/pricing/settings" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingSettingsPage)} />
        <Route path="/pricing" component={featureGated("dynamic_pricing_enabled", "dynamic_pricing", PricingPage)} />
        {/* AI Agents / Autonomous Operations (Task #2087) — literal child
            routes registered BEFORE the parent catch-all. */}
        <Route path="/ai-agents/approvals" component={featureGated("ai_agents_enabled", "ai_agents", AiAgentsPage)} />
        <Route path="/ai-agents/runs" component={featureGated("ai_agents_enabled", "ai_agents", AiAgentsPage)} />
        <Route path="/ai-agents/schedules" component={featureGated("ai_agents_enabled", "ai_agents", AiAgentsPage)} />
        <Route path="/ai-agents/settings" component={featureGated("ai_agents_enabled", "ai_agents", AiAgentsPage)} />
        <Route path="/ai-agents" component={featureGated("ai_agents_enabled", "ai_agents", AiAgentsPage)} />
        {/* HR module (Task #189) — order matters: specific before param/prefix. */}
        <Route path="/hr/overview" component={featureGated("hr_module_enabled", "hr", HrOverview, "HR Module")} />
        <Route path="/hr/me" component={featureGated("hr_module_enabled", "hr", HrMe, "HR Module")} />
        <Route path="/hr/time-off" component={featureGated("hr_module_enabled", "hr", HrTimeOff, "HR Module")} />
        <Route path="/hr/change-requests" component={featureGated("hr_module_enabled", "hr", HrChangeRequests, "HR Module")} />
        <Route path="/hr/onboarding-templates" component={featureGated("hr_module_enabled", "hr", HrOnboardingTemplates, "HR Module")} />
        <Route path="/hr/settings" component={featureGated("hr_module_enabled", "hr", HrSettings, "HR Module")} />
        <Route path="/hr/audit" component={featureGated("hr_module_enabled", "hr", HrAudit, "HR Module")} />
        <Route path="/hr/performance/staff" component={featureGated("hr_module_enabled", "hr", HrPerformanceStaff, "HR Module")} />
        <Route path="/hr/performance" component={featureGated("hr_module_enabled", "hr", HrPerformance, "HR Module")} />
        <Route path="/hr/rota" component={featureGated("hr_module_enabled", "hr", HrRota, "HR Module")} />
        <Route path="/hr/payments" component={featureGated("hr_module_enabled", "hr", HrPayments, "HR Module")} />
        <Route path="/hr/staff/:id" component={featureGated("hr_module_enabled", "hr", HrStaffDetail, "HR Module")} />
        <Route path="/hr/staff" component={featureGated("hr_module_enabled", "hr", HrStaffList, "HR Module")} />
        <Route path="/hr"><Redirect to="/hr/overview" /></Route>
        {/* Affiliate Payout Management (Task #1443) — order matters: specific before param/prefix. */}
        <Route path="/affiliates/list/:id" component={featureGated("affiliate_payouts", "affiliates", AffiliateDetail)} />
        <Route path="/affiliates/list" component={featureGated("affiliate_payouts", "affiliates", AffiliatesList)} />
        <Route path="/affiliates/rules" component={featureGated("affiliate_payouts", "affiliates", AffiliateRules)} />
        <Route path="/affiliates/payouts" component={featureGated("affiliate_payouts", "affiliates", AffiliatePayouts)} />
        <Route path="/affiliates/batches" component={featureGated("affiliate_payouts", "affiliates", AffiliateBatches)} />
        <Route path="/affiliates/statements" component={featureGated("affiliate_payouts", "affiliates", AffiliateStatements)} />
        <Route path="/affiliates/reports" component={featureGated("affiliate_payouts", "affiliates", AffiliateReports)} />
        <Route path="/affiliates/settings" component={featureGated("affiliate_payouts", "affiliates", AffiliateSettings)} />
        <Route path="/affiliates" component={featureGated("affiliate_payouts", "affiliates", AffiliatesDashboard)} />
        {/* Consignment Profit Splits (Task #1444) — order matters: specific before param/prefix. */}
        <Route path="/consignment-splits/settlements/:id" component={featureGated("consignment_profit_splits", "consignment_splits", ConsignmentSettlementDetail)} />
        <Route path="/consignment-splits/settlements" component={featureGated("consignment_profit_splits", "consignment_splits", ConsignmentSettlements)} />
        <Route path="/consignment-splits/rules" component={featureGated("consignment_profit_splits", "consignment_splits", ConsignmentSplitRules)} />
        <Route path="/consignment-splits/splits" component={featureGated("consignment_profit_splits", "consignment_splits", ConsignmentSplitCalculations)} />
        <Route path="/consignment-splits/adjustments" component={featureGated("consignment_profit_splits", "consignment_splits", ConsignmentSplitAdjustments)} />
        <Route path="/consignment-splits/reports" component={featureGated("consignment_profit_splits", "consignment_splits", ConsignmentSplitReports)} />
        <Route path="/consignment-splits/settings" component={featureGated("consignment_profit_splits", "consignment_splits", ConsignmentSplitSettings)} />
        <Route path="/consignment-splits/audit" component={featureGated("consignment_profit_splits", "consignment_splits", ConsignmentSplitAudit)} />
        <Route path="/consignment-splits" component={featureGated("consignment_profit_splits", "consignment_splits", ConsignmentSplitsDashboard)} />
        <Route component={NotFound} />
      </Switch>
      </RouteErrorBoundary>
      </Suspense>
    </MainLayout>
  );
}

// Renders the authorised route tree for signed-in users and redirects
// signed-out users to /sign-in. We deliberately do NOT use Clerk's
// <Show when="signed-in"> / <SignedIn> here, because those components
// flip their child mount/unmount on every transient change to Clerk's
// internal auth state — including the brief "loading" phase that fires
// roughly once a minute when the session JWT rotates on dev keys (and
// also during cross-origin iframe focus blips inside the workspace
// preview). Each unmount throws away the route tree's React state,
// which wipes any in-progress form (the multi-invoice purchase order
// reset bug). Instead we keep the routes mounted as long as the user
// has been signed in at any point, and only redirect to /sign-in if
// signed-out persists for a few seconds (i.e. a real sign-out, not a
// rotation blip).
function ProtectedShell() {
  const { isSignedIn, isLoaded } = useAuth();
  // Self-heal a wedged clerk-js init caused by a stale cookie from the
  // pre-migration Clerk instance (works-in-incognito-only symptom). See
  // lib/clerk-recovery.ts.
  const clerk = useClerk();
  const { stuck: clerkStuck } = useClerkLoadWatchdog(isLoaded, clerk);
  const [routerLocation] = useLocation();
  const [routerSearch] = useSearch();
  const everSignedInRef = useRef(false);
  if (isSignedIn) everSignedInRef.current = true;
  const [signedOutSustained, setSignedOutSustained] = useState(false);
  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn) {
      setSignedOutSustained(false);
      return;
    }
    if (!everSignedInRef.current) {
      // First load and the user is signed out — redirect immediately.
      setSignedOutSustained(true);
      return;
    }
    // We were signed in and Clerk now reports signed-out. Wait a few
    // seconds before tearing down the routes; if it was just a token
    // rotation blip, isSignedIn will flip back to true and we'll cancel.
    const t = setTimeout(() => setSignedOutSustained(true), 5000);
    return () => clearTimeout(t);
  }, [isSignedIn, isLoaded]);

  // Dev-only test-auth bridge (Task #2077): Clerk can't init on the local
  // preview host (pk_live is domain-locked), so skip the signed-in gate and
  // render the authorised routes directly — API calls authenticate via the
  // static dev bearer token registered by ClerkAuthBridge. Compiled to a
  // constant `false` branch in production builds.
  if (isDevTestAuthActive()) {
    return <AuthorisedRoutes />;
  }
  if (!isLoaded && !everSignedInRef.current) {
    if (clerkStuck) return <ClerkStuckRecovery />;
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }
  if (signedOutSustained && !isSignedIn) {
    // Anonymous users hitting marketing paths go to the public /site/* equivalent.
    // Authenticated users at these same paths (e.g. /sales, /about) see the app.
    const MARKETING_REDIRECTS: Record<string, string> = {
      "/": "/site",
      "/sales": "/site/sales",
      "/why-metalui": "/site/why-metalui",
      "/what-we-do": "/site/what-we-do",
      "/trial": "/site/trial",
      "/testimonials": "/site/testimonials",
      "/about": "/site/about",
      "/pricing": "/site/pricing",
      "/contact": "/site/contact",
    };
    const marketingTarget = MARKETING_REDIRECTS[routerLocation];
    if (marketingTarget) {
      return <Redirect to={marketingTarget} />;
    }
    const ringReturn = ringAppstoreAuthReturn(routerLocation, routerSearch);
    return (
      <Redirect
        to={ringReturn ? `/sign-in?dest=${encodeURIComponent(ringReturn)}` : "/sign-in"}
      />
    );
  }
  return <AuthorisedRoutes />;
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const qc = useQueryClient();
  const prev = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    return addListener(({ user }) => {
      const id = user?.id ?? null;
      if (prev.current !== undefined && prev.current !== id) qc.clear();
      prev.current = id;
    });
  }, [addListener, qc]);
  return null;
}

// Bridges Clerk's session JWT into the generated API client so that every
// request carries `Authorization: Bearer <token>`. This is what lets the app
// work inside cross-site iframes (e.g. the workspace canvas preview), where
// browsers block third-party cookies and the Clerk session cookie can't be
// sent. Must be mounted inside ClerkProvider and *before* any component that
// fires API queries, so the getter is registered before the first fetch.
//
// IMPORTANT: Clerk rotates the session JWT roughly every minute and emits a
// new `getToken` function reference each time. Earlier we listed `getToken`
// as an effect dep, which caused the cleanup/setup cycle (and a global
// `qc.invalidateQueries()`) to fire on every rotation — invalidating every
// query in the cache and briefly nulling the token getter. That manifested
// as the whole app appearing to "refresh" every ~30-60s, with form state
// getting wiped while users were typing. We now hold `getToken` in a ref
// and register the auth callback exactly once when Clerk finishes loading,
// so rotation is silent.
function ClerkAuthBridge() {
  const { getToken, isLoaded } = useAuth();
  const qc = useQueryClient();
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);
  useEffect(() => {
    // Dev-only test-auth bridge (Task #2077): on the local preview the
    // pk_live key never finishes loading, so register a static dev token
    // immediately instead of waiting for Clerk. See lib/dev-test-auth.ts.
    if (DEV_TEST_AUTH_TOKEN) {
      setAuthTokenGetter(() => Promise.resolve(DEV_TEST_AUTH_TOKEN));
      void qc.invalidateQueries();
      return () => {
        setAuthTokenGetter(null);
      };
    }
    if (!isLoaded) return;
    setAuthTokenGetter(() => getTokenRef.current());
    // Re-fire any in-flight /api/me etc. that may have raced ahead before
    // the token getter was registered. This is a one-shot — subsequent token
    // rotations are picked up via the ref above without invalidating the cache.
    void qc.invalidateQueries();
    return () => {
      setAuthTokenGetter(null);
    };
  }, [isLoaded, qc]);
  return null;
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();
  const appearance = useClerkAppearance();
  if (!clerkPubKey) {
    throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
  }
  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      appearance={appearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={{
        signIn: { start: { title: "Sign in to MetalUI", subtitle: "Access your operations workspace" } },
        signUp: { start: { title: "Create your operator account", subtitle: "Request access from your admin after signing up" } },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <ClerkAuthBridge />
      <ClerkQueryClientCacheInvalidator />
      <TenantSelectionBridge />
      <BrandingProvider>
        <TooltipProvider>
          <WalkthroughProvider>
          <HelpDrawerProvider>
            <Switch>
              <Route path="/sign-in/*?" component={SignInPage} />
              <Route path="/sign-up/*?" component={SignUpPage} />
              {OPERATOR_MARKET_ROUTES.map((path) => (
                <Route key={path} path={path} component={ProtectedShell} />
              ))}
              <Route path="/verification" component={Verification} />
              <Route component={ProtectedShell} />
            </Switch>
            <Toaster />
            <SonnerToaster />
          </HelpDrawerProvider>
          </WalkthroughProvider>
        </TooltipProvider>
      </BrandingProvider>
    </ClerkProvider>
  );
}

function PublicSurfaceRoutes() {
  return (
    <PublicBrandingProvider>
      <TooltipProvider>
        <Switch>
          <Route path="/site/*?" component={PublicSite} />
          <Route path="/markets/:slug" component={PublicMarketPage} />
          <Route path="/widgets/market/:key" component={PublicMarketWidget} />
        </Switch>
        <Toaster />
        <SonnerToaster />
      </TooltipProvider>
    </PublicBrandingProvider>
  );
}

function CustomerSurfaceRoutes() {
  return (
    <>
      <TooltipProvider>
        <Switch>
          <Route path="/wholesale/shop" component={CustomerStorefront} />
          <Route path="/account/wholesale" component={CustomerPortal} />
          <Route path="/portal/*?" component={CustomerPortal} />
          <Route path="/portfolio/*?" component={CustomerPortal} />
          <Route path="/shop/*?" component={CustomerStorefront} />
        </Switch>
        <Toaster />
        <SonnerToaster />
      </TooltipProvider>
    </>
  );
}

function AppSurfaceRoutes() {
  return (
    <AppSurfaceBoundary
      customerSurface={
        <ThemeProvider syncWithServer={false}>
          <CustomerSurfaceRoutes />
        </ThemeProvider>
      }
      publicSurface={
        <ThemeProvider syncWithServer={false}>
          <PublicSurfaceRoutes />
        </ThemeProvider>
      }
      staffSurface={
        <ThemeProvider>
          <PackingRecorderProvider>
            <ClerkProviderWithRoutes />
          </PackingRecorderProvider>
        </ThemeProvider>
      }
    />
  );
}

/**
 * Dedicated portfolio origin entry point. It intentionally never mounts
 * ClerkProvider, MainLayout, or any operator route tree. The customer portal
 * keeps its own HMAC session boundary and owns all portfolio navigation.
 *
 * `/portfolio` remains the compatibility path during local development and
 * for old bookmarks. Published MetalUI hosts redirect it to the dedicated
 * portfolio origin before React mounts (see main.tsx).
 */
function PortfolioAppEntry() {
  const [location] = useLocation();
  if (isLegacyPortalPortfolioPath(location)) {
    const search = typeof window !== "undefined" ? window.location.search : "";
    return <Redirect to={portfolioPath(canonicalPortfolioPath(location), search)} />;
  }
  if (!isPortfolioPath(location)) {
    const search = typeof window !== "undefined" ? window.location.search : "";
    return <Redirect to={portfolioPath("/portfolio", search)} />;
  }
  return <CustomerPortal />;
}

function App() {
  const portfolioSurface =
    typeof window !== "undefined" && isPortfolioAppHost(window.location.hostname);

  if (portfolioSurface) {
    return (
      <QueryClientProvider client={queryClient}>
        <WhatnotFeeInvalidationSync />
        <AppPerformanceInstrumentation fallbackDelayMs={0} />
        <ThemeProvider>
          <WouterRouter base={basePath}>
            <TooltipProvider>
              <Suspense fallback={<RouteFallback />}>
                <PortfolioAppEntry />
              </Suspense>
              <Toaster />
            </TooltipProvider>
          </WouterRouter>
        </ThemeProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <WhatnotFeeInvalidationSync />
      <AppPerformanceInstrumentation />
      <WouterRouter base={basePath}>
        <NewsiteCommerceEntry />
        <AppSurfaceRoutes />
      </WouterRouter>
    </QueryClientProvider>
  );
}

export default App;

const ExceptionRulesPage = lazy(() => import("@/pages/exceptions/rules"));

const PricingPremiumProductsPage = lazy(() => import("@/pages/pricing/premium-products"));

const PricingMarketplacePage = lazy(() => import("@/pages/pricing/marketplace"));

const PricingSpotProductsPage = lazy(() => import("@/pages/pricing/spot-products"));

const PricingScheduledPage = lazy(() => import("@/pages/pricing/scheduled"));

const PricingRecommendationsPage = lazy(() => import("@/pages/pricing/recommendations"));

const PricingHistoryPage = lazy(() => import("@/pages/pricing/history"));

const PricingSettingsPage = lazy(() => import("@/pages/pricing/settings"));

const PricingMarginProtectionPage = lazy(() => import("@/pages/pricing/margin-protection"));

const PricingExceptionsPage = lazy(() => import("@/pages/pricing/exceptions"));

const MARKET_TOOLS_FEATURE: FeatureKey = "market_tools_enabled";

const MarketsCharts = lazy(() => import("@/pages/markets/charts"));

const MarketsDealerCalculator = lazy(() => import("@/pages/markets/dealer-calculator"));

const MarketsCalculator = lazy(() => import("@/pages/markets/calculator"));

const MarketsLivePrices = lazy(() => import("@/pages/markets/live-prices"));

const MarketsConverter = lazy(() => import("@/pages/markets/converter"));

const MarketsRatios = lazy(() => import("@/pages/markets/ratios"));

const MarketsHistory = lazy(() => import("@/pages/markets/history"));

const MarketsHelp = lazy(() => import("@/pages/markets/help"));

const MarketsSettings = lazy(() => import("@/pages/markets/settings"));

const MarketAlertsContent = lazy(() => import("@/pages/markets/alerts-content"));

const SignInPage = lazy(() => import("@/pages/sign-in"));

const SignUpPage = lazy(() => import("@/pages/sign-up"));

const IntegrationLogs = lazy(() => import("@/pages/integrations/logs"));

const IntegrationDetail = lazy(() => import("@/pages/integrations/detail"));
const RingAppstoreLinkPage = lazy(() => import("@/pages/ring-appstore-link"));
const RingAppstoreHomePage = lazy(() => import("@/pages/ring-appstore-home"));
